export function createUiShell({ rootEl, leftPanelEl, rightPanelEl, bottomPanelEl, topbarEl, hudEl, mapEl, events }) {
  const MAP_MODE_KEY = "hydrotools:map-mode";
  const UI_THEME_KEY = "hydrotools:ui-theme";
  const LEFT_W_KEY = "hydrotools:leftPanelW";
  const RIGHT_W_KEY = "hydrotools:rightPanelW";
  const BOTTOM_H_KEY = "hydrotools:bottomPanelH";
  const LEFT_OPEN_KEY = "hydrotools:leftOpen";
  const RIGHT_OPEN_KEY = "hydrotools:rightOpen";
  const BOTTOM_OPEN_KEY = "hydrotools:bottomOpen";
  const COLLAPSED_TRACK_PX = 16;
  const MIN_LEFT_PANEL_PX = 260;
  const MIN_RIGHT_PANEL_PX = 280;
  const MIN_BOTTOM_PANEL_PX = 140;
  const MIN_WORKSPACE_STRIP_PX = 16;
  const PROJECT_SCHEMA = "hydrotools.project";
  const PROJECT_VERSION = 1;
  const PROJECT_DATA_POLICY_KEY = "hydrotools:project-data-policy";
  const PROJECT_EMBED_MAX_SOUNDINGS_KEY = "hydrotools:project-embed-max-soundings";
  const PROJECT_AUTOSAVE_ENABLED_KEY = "hydrotools:project-autosave-enabled";
  const PROJECT_AUTOSAVE_INTERVAL_KEY = "hydrotools:project-autosave-interval-sec";
  const PROJECT_AUTOSAVE_SNAPSHOT_KEY = "hydrotools:project-autosave-snapshot:v1";
  const PROJECT_AUTOSAVE_AT_KEY = "hydrotools:project-autosave-at:v1";
  const FULLSCREEN_SIM_HANDOFF_KEY = "hydrotools:fullscreen-handoff:simulator:v1";

  let themeToggleBtn = null;
  let captureBtn = null;
  let captureClipboardBtn = null;
  let settingsModalEl = null;
  let settingsPanelEl = null;
  let settingsOpen = false;
  let runtimeRef = null;
  let lastRestoreReport = null;
  let relinkItems = [];
  let pendingRelinkRef = null;
  let autosaveTimer = null;
  let fullscreenBtnEl = null;
  let fullscreenExitBtnEl = null;
  let fullscreenBound = false;
  let fileDropOverlayEl = null;
  let fileDropDepth = 0;
  const bottomPanelEntries = new Map();
  let activeBottomPanelId = "";

  const UI_THEMES = [
    { id: "deep-blue", name: "Deep Blue" },
    { id: "steel-harbor", name: "Steel Harbor" },
    { id: "survey-slate", name: "Survey Slate" },
    { id: "red-watch", name: "Red Watch" },
    { id: "matrix-green", name: "Matrix Green" },
    { id: "rose-mist", name: "Rose Mist" },
    { id: "daylight", name: "Daylight" },
    { id: "sunset", name: "Sunset" },
    { id: "twilight", name: "Twilight" },
  ];

  function dispatchMapResizePulse() {
    setTimeout(() => window.dispatchEvent(new Event("resize")), 0);
    setTimeout(() => window.dispatchEvent(new Event("resize")), 180);
    setTimeout(() => window.dispatchEvent(new Event("resize")), 360);
  }

  function applyMapMode(mode, emit = true) {
    const nextMode = mode === "dark" ? "dark" : "light";
    localStorage.setItem(MAP_MODE_KEY, nextMode);
    if (mapEl) mapEl.dataset.mapMode = nextMode;
    if (themeToggleBtn) {
      const target = nextMode === "dark" ? "light" : "dark";
      themeToggleBtn.dataset.mode = nextMode;
      themeToggleBtn.title = `Switch to ${target} map`;
      themeToggleBtn.setAttribute("aria-label", themeToggleBtn.title);
    }
    if (emit) events.emit("map:basemap:mode", { mode: nextMode });
  }

  function applyUiTheme(themeId) {
    const valid = UI_THEMES.some((t) => t.id === themeId);
    const next = valid ? themeId : "deep-blue";
    rootEl.dataset.uiTheme = next;
    localStorage.setItem(UI_THEME_KEY, next);
    const sel = settingsModalEl?.querySelector?.("#settings-ui-theme");
    if (sel) sel.value = next;
  }

  function ensureThemeToggle() {
    if (!mapEl?.parentElement || themeToggleBtn) return;
    themeToggleBtn = document.createElement("button");
    themeToggleBtn.type = "button";
    themeToggleBtn.className = "map-theme-toggle";
    themeToggleBtn.id = "map-theme-toggle";
    themeToggleBtn.innerHTML = `
      <span class="map-theme-icon sun" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="M12 4a1 1 0 0 1 1 1v1a1 1 0 1 1-2 0V5a1 1 0 0 1 1-1Zm0 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm8-6a1 1 0 0 1-1 1h-1a1 1 0 1 1 0-2h1a1 1 0 0 1 1 1ZM7 12a1 1 0 0 1-1 1H5a1 1 0 1 1 0-2h1a1 1 0 0 1 1 1Zm9.66 5.24a1 1 0 0 1 1.41 0l.7.7a1 1 0 1 1-1.41 1.42l-.7-.71a1 1 0 0 1 0-1.41Zm-10.61 0a1 1 0 0 1 0 1.41l-.7.71a1 1 0 1 1-1.42-1.42l.71-.7a1 1 0 0 1 1.41 0Zm12.72-11.9a1 1 0 0 1 0 1.42l-.7.7a1 1 0 0 1-1.41-1.41l.7-.71a1 1 0 0 1 1.41 0ZM5.35 5.34a1 1 0 0 1 1.41 0l.7.71a1 1 0 0 1-1.41 1.41l-.71-.7a1 1 0 0 1 0-1.42Z"/>
        </svg>
      </span>
      <span class="map-theme-icon moon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
          <path d="M14.94 3.5a1 1 0 0 1 .54 1.87A7.5 7.5 0 1 0 18.63 15a1 1 0 1 1 1.4 1.44A9.5 9.5 0 1 1 13.5 3.02a1 1 0 0 1 1.44.48Z"/>
        </svg>
      </span>
    `;
    themeToggleBtn.addEventListener("click", () => {
      const current = themeToggleBtn?.dataset?.mode === "dark" ? "dark" : "light";
      applyMapMode(current === "dark" ? "light" : "dark");
    });
    mapEl.parentElement.appendChild(themeToggleBtn);
    const saved = localStorage.getItem(MAP_MODE_KEY);
    applyMapMode(saved === "light" ? "light" : "dark", false);
  }

  function formatCaptureTimestamp() {
    const now = new Date();
    const pad = (value) => String(value).padStart(2, "0");
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
  }

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function escapeUiText(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");
  }

  function renderBottomPanelHost() {
    const entries = [...bottomPanelEntries.values()];
    if (!entries.length) {
      bottomPanelEl.innerHTML = `<div class="panel-content"><div class="small">Bottom panel ready.</div></div>`;
      addResizer(bottomPanelEl, "top", "--bottomPanelH", BOTTOM_H_KEY);
      return;
    }
    if (!entries.some((entry) => entry.id === activeBottomPanelId)) {
      activeBottomPanelId = entries[entries.length - 1]?.id || "";
    }
    const activeEntry = entries.find((entry) => entry.id === activeBottomPanelId) || entries[entries.length - 1];
    const tabsHtml = entries.length > 1
      ? `
        <div class="bottom-panel-tabs" role="tablist" aria-label="Bottom panel toolsets">
          ${entries.map((entry) => `
            <button
              type="button"
              class="bottom-panel-tab ${entry.id === activeEntry.id ? "is-active" : ""}"
              data-bottom-panel-tab="${escapeUiText(entry.id)}"
              role="tab"
              aria-selected="${entry.id === activeEntry.id ? "true" : "false"}"
            >${escapeUiText(entry.title || entry.id)}</button>
          `).join("")}
        </div>
      `
      : "";
    bottomPanelEl.innerHTML = `
      <div class="panel-content bottom-panel-stack">
        ${tabsHtml}
        <div class="bottom-panel-body"></div>
      </div>
    `;
    const bodyHost = bottomPanelEl.querySelector(".bottom-panel-body");
    if (bodyHost && activeEntry?.bodyEl) bodyHost.appendChild(activeEntry.bodyEl);
    bottomPanelEl.querySelectorAll("[data-bottom-panel-tab]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const nextId = String(btn.getAttribute("data-bottom-panel-tab") || "");
        if (!nextId || nextId === activeBottomPanelId) return;
        activeBottomPanelId = nextId;
        renderBottomPanelHost();
      });
    });
    try {
      activeEntry?.onShow?.();
    } catch (err) {
      console.error("[UI] bottom panel onShow failed", err);
    }
    addResizer(bottomPanelEl, "top", "--bottomPanelH", BOTTOM_H_KEY);
  }

  function canvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Unable to create image export."));
      }, "image/png");
    });
  }

  function setCaptureButtonsBusy(isBusy) {
    [captureBtn, captureClipboardBtn].forEach((btn) => {
      if (!btn) return;
      btn.dataset.busy = isBusy ? "true" : "false";
      btn.disabled = !!isBusy;
    });
  }

  async function renderMapCaptureBlob() {
    const captureHost = mapEl?.parentElement;
    if (!mapEl || !captureHost) {
      throw new Error("Map capture is unavailable");
    }
    const mapInstance = runtimeRef?.map?.instance;
    mapInstance?.invalidateSize?.(false);
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const html2canvas = (await import("html2canvas")).default;
    const canvas = await html2canvas(captureHost, {
      backgroundColor: null,
      useCORS: true,
      logging: false,
      scale: Math.max(1, Math.min(4, window.devicePixelRatio || 2)),
      ignoreElements(element) {
        if (!(element instanceof Element)) return false;
        if (element.closest("#hud")) return true;
        if (element.closest("#map-theme-toggle")) return true;
        if (element.closest("#map-capture-toggle")) return true;
        if (element.closest("#map-capture-clipboard-toggle")) return true;
        if (element.closest(".map-measure-control")) return true;
        if (element.closest(".map-search-control")) return true;
        if (element.closest(".map-rotate-control")) return true;
        if (element.closest(".leaflet-control-zoom")) return true;
        return false;
      },
    });
    return canvasToBlob(canvas);
  }

  async function captureMapViewport() {
    if (captureBtn?.dataset?.busy === "true" || captureClipboardBtn?.dataset?.busy === "true") return;
    try {
      setCaptureButtonsBusy(true);
      const blob = await renderMapCaptureBlob();
      downloadBlob(blob, `hydrotools-map-capture_${formatCaptureTimestamp()}.png`);
      toast("Map capture saved");
    } catch (err) {
      console.error("[UI] map capture failed", err);
      toast(err?.message || "Map capture failed");
    } finally {
      setCaptureButtonsBusy(false);
    }
  }

  async function copyMapViewportToClipboard() {
    if (captureBtn?.dataset?.busy === "true" || captureClipboardBtn?.dataset?.busy === "true") return;
    try {
      if (!window.isSecureContext) {
        throw new Error("Clipboard image copy requires HTTPS or localhost");
      }
      if (typeof ClipboardItem === "undefined" || !navigator?.clipboard?.write) {
        throw new Error("Image clipboard copy is not supported in this browser");
      }
      setCaptureButtonsBusy(true);
      const blob = await renderMapCaptureBlob();
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type || "image/png"]: blob,
        }),
      ]);
      toast("Map capture copied to clipboard");
    } catch (err) {
      console.error("[UI] map capture clipboard copy failed", err);
      const msg = String(err?.message || "Clipboard copy failed");
      if (msg.includes("HTTPS or localhost")) {
        toast("Clipboard copy needs HTTPS or localhost. Saved PNG instead.");
        try {
          const blob = await renderMapCaptureBlob();
          downloadBlob(blob, `hydrotools-map-capture_${formatCaptureTimestamp()}.png`);
        } catch {}
      } else {
        toast(msg);
      }
    } finally {
      setCaptureButtonsBusy(false);
    }
  }

  function ensureCaptureButton() {
    if (!mapEl?.parentElement || captureBtn || captureClipboardBtn) return;
    captureBtn = document.createElement("button");
    captureBtn.type = "button";
    captureBtn.className = "map-measure-btn map-capture-btn";
    captureBtn.id = "map-capture-toggle";
    captureBtn.title = "Save map capture";
    captureBtn.setAttribute("aria-label", captureBtn.title);
    captureBtn.dataset.busy = "false";
    captureBtn.innerHTML = `
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path d="M7.5 7.5 9 5h6l1.5 2.5H19A2.5 2.5 0 0 1 21.5 10v7A2.5 2.5 0 0 1 19 19.5H5A2.5 2.5 0 0 1 2.5 17v-7A2.5 2.5 0 0 1 5 7.5h2.5Zm4.5 8.2a3.7 3.7 0 1 0 0-7.4 3.7 3.7 0 0 0 0 7.4Zm0-1.8a1.9 1.9 0 1 1 0-3.8 1.9 1.9 0 0 1 0 3.8Z"/>
      </svg>
    `;
    captureBtn.addEventListener("click", () => {
      captureMapViewport();
    });
    mapEl.parentElement.appendChild(captureBtn);

    captureClipboardBtn = document.createElement("button");
    captureClipboardBtn.type = "button";
    captureClipboardBtn.className = "map-measure-btn map-capture-btn map-capture-clipboard-btn";
    captureClipboardBtn.id = "map-capture-clipboard-toggle";
    captureClipboardBtn.title = "Copy map capture to clipboard";
    captureClipboardBtn.setAttribute("aria-label", captureClipboardBtn.title);
    captureClipboardBtn.dataset.busy = "false";
    captureClipboardBtn.innerHTML = `
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path d="M9 3.5A2.5 2.5 0 0 0 6.5 6v11A2.5 2.5 0 0 0 9 19.5h8A2.5 2.5 0 0 0 19.5 17V6A2.5 2.5 0 0 0 17 3.5h-1.26A2.75 2.75 0 0 0 13.1 2h-.2a2.75 2.75 0 0 0-2.64 1.5H9Zm2.1.3c.13 0 .24-.08.29-.2.2-.47.66-.8 1.21-.8h.2c.55 0 1.01.33 1.21.8.05.12.16.2.29.2H17A1.7 1.7 0 0 1 18.7 5.5v12A1.7 1.7 0 0 1 17 19.2H9A1.7 1.7 0 0 1 7.3 17.5v-12A1.7 1.7 0 0 1 9 3.8h2.1Zm-4.8 3.4a.75.75 0 0 1 .75.75v10.55c0 .41.34.75.75.75h7.95a.75.75 0 1 1 0 1.5H7.8a2.25 2.25 0 0 1-2.25-2.25V7.95a.75.75 0 0 1 .75-.75Z"/>
      </svg>
    `;
    captureClipboardBtn.addEventListener("click", () => {
      copyMapViewportToClipboard();
    });
    mapEl.parentElement.appendChild(captureClipboardBtn);
  }

  function panelSizeNumber(cssVarName, storageKey, fallbackPx) {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(cssVarName)?.trim()
      || localStorage.getItem(storageKey)
      || "";
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) && n > 0 ? n : fallbackPx;
  }

  function persistPanelSize(cssVarName, storageKey, px) {
    const rounded = `${Math.max(0, Math.round(Number(px) || 0))}px`;
    document.documentElement.style.setProperty(cssVarName, rounded);
    localStorage.setItem(storageKey, rounded);
    return rounded;
  }

  function appBodyWidth() {
    return Math.max(0, Number(rootEl?.querySelector?.(".app-body")?.getBoundingClientRect?.().width || window.innerWidth || 0));
  }

  function centerStackHeight() {
    return Math.max(0, Number(rootEl?.querySelector?.(".center-stack")?.getBoundingClientRect?.().height || window.innerHeight || 0));
  }

  function constrainHorizontalPanels(primary, desiredPx) {
    const bodyW = appBodyWidth();
    if (!bodyW) return;
    const leftOpen = isDockOpen("left");
    const rightOpen = isDockOpen("right");
    const leftTrack = leftOpen ? panelSizeNumber("--leftPanelW", LEFT_W_KEY, 360) : COLLAPSED_TRACK_PX;
    const rightTrack = rightOpen ? panelSizeNumber("--rightPanelW", RIGHT_W_KEY, 420) : COLLAPSED_TRACK_PX;
    const availableForPanels = Math.max(0, bodyW - MIN_WORKSPACE_STRIP_PX);

    if (primary === "left") {
      let nextLeft = Math.max(MIN_LEFT_PANEL_PX, Math.min(Number(desiredPx) || 0, availableForPanels - (rightOpen ? MIN_RIGHT_PANEL_PX : COLLAPSED_TRACK_PX)));
      if (rightOpen && nextLeft + rightTrack > availableForPanels) {
        const nextRight = Math.max(MIN_RIGHT_PANEL_PX, availableForPanels - nextLeft);
        persistPanelSize("--rightPanelW", RIGHT_W_KEY, nextRight);
        if (nextLeft + nextRight > availableForPanels) nextLeft = Math.max(MIN_LEFT_PANEL_PX, availableForPanels - nextRight);
      }
      persistPanelSize("--leftPanelW", LEFT_W_KEY, nextLeft);
      return;
    }

    if (primary === "right") {
      let nextRight = Math.max(MIN_RIGHT_PANEL_PX, Math.min(Number(desiredPx) || 0, availableForPanels - (leftOpen ? MIN_LEFT_PANEL_PX : COLLAPSED_TRACK_PX)));
      if (leftOpen && leftTrack + nextRight > availableForPanels) {
        const nextLeft = Math.max(MIN_LEFT_PANEL_PX, availableForPanels - nextRight);
        persistPanelSize("--leftPanelW", LEFT_W_KEY, nextLeft);
        if (nextLeft + nextRight > availableForPanels) nextRight = Math.max(MIN_RIGHT_PANEL_PX, availableForPanels - nextLeft);
      }
      persistPanelSize("--rightPanelW", RIGHT_W_KEY, nextRight);
    }
  }

  function constrainBottomPanel(desiredPx) {
    const stackH = centerStackHeight();
    const maxH = Math.max(MIN_BOTTOM_PANEL_PX, stackH - MIN_WORKSPACE_STRIP_PX);
    persistPanelSize("--bottomPanelH", BOTTOM_H_KEY, Math.max(MIN_BOTTOM_PANEL_PX, Math.min(Number(desiredPx) || 0, maxH)));
  }

  function reconcilePanelSizes() {
    if (isDockOpen("left")) constrainHorizontalPanels("left", panelSizeNumber("--leftPanelW", LEFT_W_KEY, 360));
    if (isDockOpen("right")) constrainHorizontalPanels("right", panelSizeNumber("--rightPanelW", RIGHT_W_KEY, 420));
    if (isDockOpen("bottom")) constrainBottomPanel(panelSizeNumber("--bottomPanelH", BOTTOM_H_KEY, 280));
  }

  function addResizer(panelEl, side, cssVarName, storageKey) {
    if (!panelEl || panelEl.querySelector(`.panel-resizer.${side}`)) return;
    const grip = document.createElement("div");
    grip.className = `panel-resizer ${side}`;
    grip.style.zIndex = "3000";
    grip.style.touchAction = "none";
    panelEl.appendChild(grip);
    const saved = localStorage.getItem(storageKey);
    if (saved) document.documentElement.style.setProperty(cssVarName, saved);
    reconcilePanelSizes();

    let dragging = false;
    let maybeClick = false;
    let moved = false;
    let startX = 0;
    let startY = 0;

    const onMove = (e) => {
      if (!dragging) return;
      if (maybeClick) {
        const dx = Math.abs(e.clientX - startX);
        const dy = Math.abs(e.clientY - startY);
        if (dx > 4 || dy > 4) {
          moved = true;
          maybeClick = false;
        }
      }
      if (side === "left") {
        constrainHorizontalPanels("left", e.clientX);
      } else if (side === "right") {
        constrainHorizontalPanels("right", window.innerWidth - e.clientX);
      } else if (side === "top") {
        constrainBottomPanel(window.innerHeight - e.clientY);
      }
    };

    const stop = () => {
      if (!dragging) return;
      dragging = false;
      document.body.classList.remove("resizing");
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      if (!moved) {
        if (side === "left") setDockOpen("left", !isDockOpen("left"));
        else if (side === "right") setDockOpen("right", !isDockOpen("right"));
        else if (side === "top") setDockOpen("bottom", !isDockOpen("bottom"));
      }
      reconcilePanelSizes();
      maybeClick = false;
      moved = false;
      dispatchMapResizePulse();
    };

    grip.addEventListener("pointerdown", (e) => {
      dragging = true;
      maybeClick = true;
      moved = false;
      startX = e.clientX;
      startY = e.clientY;
      document.body.classList.add("resizing");
      grip.setPointerCapture?.(e.pointerId);
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", stop);
      window.addEventListener("pointercancel", stop);
    });
  }

  function setDockOpen(which, open) {
    if (which === "left") rootEl.classList.toggle("left-collapsed", !open);
    if (which === "right") rootEl.classList.toggle("right-collapsed", !open);
    if (which === "bottom") rootEl.classList.toggle("bottom-collapsed", !open);
    localStorage.setItem(which === "left" ? LEFT_OPEN_KEY : which === "right" ? RIGHT_OPEN_KEY : BOTTOM_OPEN_KEY, open ? "1" : "0");
    reconcilePanelSizes();
    dispatchMapResizePulse();
  }

  function isDockOpen(which) {
    return !rootEl.classList.contains(`${which}-collapsed`);
  }

  function ensureDockToggles() {
    if (!rootEl) return;
    const leftOpen = localStorage.getItem(LEFT_OPEN_KEY);
    const rightOpen = localStorage.getItem(RIGHT_OPEN_KEY);
    const bottomOpen = localStorage.getItem(BOTTOM_OPEN_KEY);
    setDockOpen("left", leftOpen !== "0");
    setDockOpen("right", rightOpen !== "0");
    setDockOpen("bottom", bottomOpen !== "0");
  }

  function closeSettingsModal() {
    if (!settingsModalEl || !settingsPanelEl || !settingsOpen) return;
    settingsOpen = false;
    settingsModalEl.setAttribute("aria-hidden", "true");
    settingsPanelEl.setAttribute("aria-hidden", "true");
    document.body.classList.remove("settings-open");
  }

  function openSettingsModal() {
    if (!settingsModalEl || !settingsPanelEl || settingsOpen) return;
    settingsOpen = true;
    settingsModalEl.setAttribute("aria-hidden", "false");
    settingsPanelEl.setAttribute("aria-hidden", "false");
    document.body.classList.add("settings-open");
    renderRestoreReport(lastRestoreReport);
    updateProjectEstimate();
    const themeSel = settingsModalEl.querySelector("#settings-ui-theme");
    if (themeSel) themeSel.value = rootEl?.dataset?.uiTheme || localStorage.getItem(UI_THEME_KEY) || "deep-blue";
    const stampEl = settingsModalEl.querySelector("#settings-project-autosave-stamp");
    if (stampEl) stampEl.textContent = localStorage.getItem(PROJECT_AUTOSAVE_AT_KEY) || "never";
  }

  function normalizeRestoreReport(report) {
    const base = report && typeof report === "object" ? report : {};
    const unresolved = Array.isArray(base.unresolved) ? base.unresolved : [];
    const errors = Array.isArray(base.errors) ? base.errors : [];
    return {
      restored: Number(base.restored || 0),
      unresolved,
      errors,
      byTool: base.byTool && typeof base.byTool === "object" ? base.byTool : {},
    };
  }

  function renderRestoreReport(report) {
    const host = settingsModalEl?.querySelector?.("#settings-restore-report");
    if (!host) return;
    if (!report) {
      host.innerHTML = `<div class="small">No project load report yet.</div>`;
      relinkItems = [];
      return;
    }
    const r = normalizeRestoreReport(report);
    relinkItems = r.unresolved.slice();
    const unresolvedRows = relinkItems.length
      ? relinkItems
          .map((u, idx) => `
            <div class="row" style="justify-content:space-between; gap:8px; margin-top:6px; padding:6px 8px; border:1px solid rgba(148,163,184,.22); border-radius:8px;">
              <div style="min-width:0;">
                <div style="font-size:12px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${u.label || u.referenceId || "Unresolved source"}</div>
                <div class="small">${u.hint || "Source missing, relink required."}${u.expectedFileName ? ` Expected: ${u.expectedFileName}` : ""}</div>
              </div>
              <button type="button" class="btn" data-project-relink="${idx}">Relink</button>
            </div>
          `)
          .join("")
      : `<div class="small">No unresolved sources.</div>`;
    const errorRows = r.errors.length
      ? `<div class="small" style="margin-top:6px;">Errors: ${r.errors.map((e) => e.message || e).slice(0, 3).join(" | ")}</div>`
      : "";
    host.innerHTML = `
      <div class="small">Restored <strong>${r.restored.toLocaleString()}</strong> item(s), unresolved <strong>${r.unresolved.length.toLocaleString()}</strong>.</div>
      ${unresolvedRows}
      ${errorRows}
    `;
  }

  function getPanelSize(varName, fallback = "") {
    const computed = getComputedStyle(document.documentElement).getPropertyValue(varName)?.trim();
    if (computed) return computed;
    return localStorage.getItem(fallback) || "";
  }

  function getProjectSaveOptions() {
    const dataPolicy = String(settingsModalEl?.querySelector?.("#settings-project-data-policy")?.value || localStorage.getItem(PROJECT_DATA_POLICY_KEY) || "reference");
    const embedMaxSoundingPoints = Math.max(0, Number(settingsModalEl?.querySelector?.("#settings-project-embed-max")?.value || localStorage.getItem(PROJECT_EMBED_MAX_SOUNDINGS_KEY) || 0) || 0);
    localStorage.setItem(PROJECT_DATA_POLICY_KEY, dataPolicy);
    localStorage.setItem(PROJECT_EMBED_MAX_SOUNDINGS_KEY, String(embedMaxSoundingPoints));
    return { dataPolicy, embedMaxSoundingPoints };
  }

  function buildProjectPayload(options = null) {
    const saveOptions = options || getProjectSaveOptions();
    const dataPolicy = String(saveOptions.dataPolicy || "reference");
    const mapInstance = runtimeRef?.map?.instance;
    const center = mapInstance?.getCenter?.();
    const view = center ? { center: [center.lat, center.lng], zoom: mapInstance.getZoom?.() } : null;
    return {
      schema: PROJECT_SCHEMA,
      version: PROJECT_VERSION,
      savedAt: new Date().toISOString(),
      project: {
        dataPolicy,
        embedMaxSoundingPoints: Number(saveOptions.embedMaxSoundingPoints || 0),
      },
      ui: {
        theme: rootEl?.dataset?.uiTheme || localStorage.getItem(UI_THEME_KEY) || "deep-blue",
        mapMode: mapEl?.dataset?.mapMode || localStorage.getItem(MAP_MODE_KEY) || "light",
        docks: {
          leftOpen: isDockOpen("left"),
          rightOpen: isDockOpen("right"),
          bottomOpen: isDockOpen("bottom"),
        },
        sizes: {
          leftPanelW: getPanelSize("--leftPanelW", LEFT_W_KEY),
          rightPanelW: getPanelSize("--rightPanelW", RIGHT_W_KEY),
          bottomPanelH: getPanelSize("--bottomPanelH", BOTTOM_H_KEY),
        },
      },
      map: view ? { view } : {},
      tools: runtimeRef?.tools?.exportProjectData?.({
        dataPolicy,
        embedMaxSoundingPoints: Number(saveOptions.embedMaxSoundingPoints || 0),
      }) || {},
    };
  }

  function estimateProjectSizeBytes(payload) {
    const json = JSON.stringify(payload);
    return new Blob([json]).size;
  }

  function formatBytes(bytes) {
    const n = Number(bytes) || 0;
    if (n < 1024) return `${n} B`;
    const kb = n / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(2)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  }

  function updateProjectEstimate() {
    const label = settingsModalEl?.querySelector?.("#settings-project-estimate");
    if (!label) return;
    try {
      const payload = buildProjectPayload();
      const size = estimateProjectSizeBytes(payload);
      label.textContent = `Estimated size: ${formatBytes(size)}`;
      label.style.color = size > (25 * 1024 * 1024) ? "#fca5a5" : "";
    } catch {
      label.textContent = "Estimated size: n/a";
    }
  }

  async function saveProjectFile() {
    try {
      const payload = buildProjectPayload();
      const size = estimateProjectSizeBytes(payload);
      if (size > (25 * 1024 * 1024) && payload?.project?.dataPolicy === "embed") {
        const ok = window.confirm(`Project is large (${formatBytes(size)}). Continue saving?`);
        if (!ok) return;
      }
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `hydrotools-project-${stamp}.hydroproj.json`;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast("Project saved");
    } catch (error) {
      console.error("[Settings] project save failed", error);
      toast("Project save failed");
    }
  }

  async function saveProjectBundleFile() {
    try {
      const payload = buildProjectPayload();
      const size = estimateProjectSizeBytes(payload);
      if (size > (25 * 1024 * 1024) && payload?.project?.dataPolicy === "embed") {
        const ok = window.confirm(`Bundle is large (${formatBytes(size)}). Continue saving?`);
        if (!ok) return;
      }
      const { default: JSZip } = await import("jszip");
      const zip = new JSZip();
      zip.file("project.hydroproj.json", JSON.stringify(payload, null, 2));
      const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `hydrotools-project-${stamp}.hydroproj.zip`;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast("Project bundle saved");
    } catch (error) {
      console.error("[Settings] project bundle save failed", error);
      toast("Bundle save failed");
    }
  }

  function migrateProjectData(input) {
    if (!input || typeof input !== "object") throw new Error("Invalid project file structure.");
    const out = { ...input };
    const hasSchema = typeof out.schema === "string" && out.schema.trim().length > 0;
    const hasVersion = Number.isFinite(Number(out.version));
    if (!hasSchema && !hasVersion) {
      // Legacy scaffold fallback.
      return {
        ...out,
        schema: PROJECT_SCHEMA,
        version: PROJECT_VERSION,
      };
    }
    if (hasSchema && out.schema !== PROJECT_SCHEMA) {
      throw new Error(`Unsupported project schema "${out.schema}".`);
    }
    const rawVersion = Number(out.version);
    if (!Number.isInteger(rawVersion) || rawVersion <= 0) {
      throw new Error("Project version is invalid.");
    }
    if (rawVersion > PROJECT_VERSION) {
      throw new Error(`Project version ${rawVersion} is newer than this app (${PROJECT_VERSION}).`);
    }
    // Placeholder migration chain for future format changes.
    let current = { ...out, schema: PROJECT_SCHEMA, version: rawVersion };
    while (current.version < PROJECT_VERSION) {
      switch (current.version) {
        default:
          throw new Error(`No migration path from version ${current.version}.`);
      }
    }
    return current;
  }

  function validateProjectData(data) {
    if (!data || typeof data !== "object") throw new Error("Project payload must be an object.");
    if (data.schema !== PROJECT_SCHEMA) throw new Error(`Project schema must be "${PROJECT_SCHEMA}".`);
    if (!Number.isInteger(data.version) || data.version !== PROJECT_VERSION) {
      throw new Error(`Project version must be ${PROJECT_VERSION}.`);
    }
    if (data.ui && typeof data.ui !== "object") throw new Error("Project ui section is invalid.");
    if (data.map && typeof data.map !== "object") throw new Error("Project map section is invalid.");
    if (data.tools && typeof data.tools !== "object") throw new Error("Project tools section is invalid.");
    if (data.project && typeof data.project !== "object") throw new Error("Project project section is invalid.");
  }

  function applyProjectData(data) {
    const normalized = migrateProjectData(data);
    validateProjectData(normalized);

    const ui = normalized.ui && typeof normalized.ui === "object" ? normalized.ui : {};
    const docks = ui.docks && typeof ui.docks === "object" ? ui.docks : {};
    const sizes = ui.sizes && typeof ui.sizes === "object" ? ui.sizes : {};
    const dataPolicy = String(normalized?.project?.dataPolicy || localStorage.getItem(PROJECT_DATA_POLICY_KEY) || "reference");
    localStorage.setItem(PROJECT_DATA_POLICY_KEY, dataPolicy);
    const policySel = settingsModalEl?.querySelector?.("#settings-project-data-policy");
    if (policySel) policySel.value = dataPolicy;

    if (ui.theme) applyUiTheme(ui.theme);
    if (ui.mapMode) applyMapMode(ui.mapMode);

    if (sizes.leftPanelW) {
      document.documentElement.style.setProperty("--leftPanelW", String(sizes.leftPanelW));
      localStorage.setItem(LEFT_W_KEY, String(sizes.leftPanelW));
    }
    if (sizes.rightPanelW) {
      document.documentElement.style.setProperty("--rightPanelW", String(sizes.rightPanelW));
      localStorage.setItem(RIGHT_W_KEY, String(sizes.rightPanelW));
    }
    if (sizes.bottomPanelH) {
      document.documentElement.style.setProperty("--bottomPanelH", String(sizes.bottomPanelH));
      localStorage.setItem(BOTTOM_H_KEY, String(sizes.bottomPanelH));
    }

    if (typeof docks.leftOpen === "boolean") setDockOpen("left", docks.leftOpen);
    if (typeof docks.rightOpen === "boolean") setDockOpen("right", docks.rightOpen);
    if (typeof docks.bottomOpen === "boolean") setDockOpen("bottom", docks.bottomOpen);

    const view = normalized.map?.view;
    if (view && Array.isArray(view.center) && view.center.length >= 2 && Number.isFinite(view.zoom)) {
      runtimeRef?.map?.instance?.setView?.([view.center[0], view.center[1]], view.zoom, { animate: false });
    }

    let restoreReport = null;
    if (normalized.tools && runtimeRef?.tools?.importProjectData) {
      restoreReport = runtimeRef.tools.importProjectData(normalized.tools, { dataPolicy });
    }
    lastRestoreReport = normalizeRestoreReport(restoreReport);
    renderRestoreReport(lastRestoreReport);
    events.emit("project:restore:report", lastRestoreReport);

    dispatchMapResizePulse();
  }

  async function readProjectFromZipFile(file) {
    const { default: JSZip } = await import("jszip");
    const zip = await JSZip.loadAsync(file);
    const jsonEntry = Object.values(zip.files).find((f) => !f.dir && /\.hydroproj\.json$/i.test(f.name))
      || Object.values(zip.files).find((f) => !f.dir && /\.json$/i.test(f.name));
    if (!jsonEntry) throw new Error("No project JSON found in zip.");
    const raw = await jsonEntry.async("string");
    return JSON.parse(raw);
  }

  function loadProjectFile(file) {
    if (!file) return;
    const isZip = /\.zip$/i.test(file.name || "");
    if (isZip) {
      readProjectFromZipFile(file)
        .then((parsed) => {
          applyProjectData(parsed);
          toast("Project bundle loaded");
        })
        .catch((error) => {
          console.error("[Settings] project bundle load failed", error);
          const msg = String(error?.message || "Project bundle load failed");
          toast(msg.length > 140 ? `${msg.slice(0, 137)}...` : msg);
        });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const raw = String(reader.result || "");
        const parsed = JSON.parse(raw);
        applyProjectData(parsed);
        toast("Project loaded");
      } catch (error) {
        console.error("[Settings] project load failed", error);
        const msg = String(error?.message || "Project load failed");
        toast(msg.length > 140 ? `${msg.slice(0, 137)}...` : msg);
      }
    };
    reader.onerror = () => {
      toast("Project load failed");
    };
    reader.readAsText(file);
  }

  function saveAutosaveSnapshot() {
    try {
      const payload = buildProjectPayload();
      localStorage.setItem(PROJECT_AUTOSAVE_SNAPSHOT_KEY, JSON.stringify(payload));
      localStorage.setItem(PROJECT_AUTOSAVE_AT_KEY, new Date().toISOString());
      const stampEl = settingsModalEl?.querySelector?.("#settings-project-autosave-stamp");
      if (stampEl) stampEl.textContent = localStorage.getItem(PROJECT_AUTOSAVE_AT_KEY) || "never";
    } catch (error) {
      console.warn("[Settings] autosave failed", error);
    }
  }

  function restoreAutosaveSnapshot() {
    try {
      const raw = localStorage.getItem(PROJECT_AUTOSAVE_SNAPSHOT_KEY);
      if (!raw) {
        toast("No autosave snapshot found");
        return;
      }
      const parsed = JSON.parse(raw);
      applyProjectData(parsed);
      toast("Autosave restored");
    } catch (error) {
      console.error("[Settings] autosave restore failed", error);
      toast("Autosave restore failed");
    }
  }

  function clearAutosaveSnapshot() {
    localStorage.removeItem(PROJECT_AUTOSAVE_SNAPSHOT_KEY);
    localStorage.removeItem(PROJECT_AUTOSAVE_AT_KEY);
    const stampEl = settingsModalEl?.querySelector?.("#settings-project-autosave-stamp");
    if (stampEl) stampEl.textContent = "never";
    toast("Autosave snapshot cleared");
  }

  function configureAutosave() {
    if (autosaveTimer) {
      clearInterval(autosaveTimer);
      autosaveTimer = null;
    }
    const enabled = localStorage.getItem(PROJECT_AUTOSAVE_ENABLED_KEY) === "1";
    const sec = Math.max(15, Number(localStorage.getItem(PROJECT_AUTOSAVE_INTERVAL_KEY) || 90) || 90);
    if (!enabled) return;
    autosaveTimer = setInterval(() => {
      saveAutosaveSnapshot();
    }, sec * 1000);
  }

  function ensureSettingsModal() {
    if (settingsModalEl && settingsPanelEl) return;
    settingsModalEl = document.createElement("div");
    settingsModalEl.className = "settings-backdrop";
    settingsModalEl.setAttribute("aria-hidden", "true");
    settingsModalEl.innerHTML = `
      <div class="settings-modal" role="dialog" aria-modal="true" aria-labelledby="settings-title" aria-hidden="true">
        <div class="settings-head">
          <div>
            <div id="settings-title" class="settings-title">Global Settings</div>
            <div class="small">HydroTools workspace-level preferences</div>
          </div>
          <button type="button" class="iconbtn" id="settings-close" title="Close">x</button>
        </div>
        <div class="settings-body">
          <div class="settings-section">
            <div class="settings-section-title">Appearance</div>
            <label class="small" for="settings-ui-theme">Theme</label>
            <select id="settings-ui-theme" class="settings-theme-select" style="margin-top:6px; min-width:220px;">
              ${UI_THEMES.map((t) => `<option value="${t.id}">${t.name}</option>`).join("")}
            </select>
            <div class="small" style="margin-top:6px;">Theme changes apply globally to panels, menus, and controls.</div>
          </div>
          <div class="settings-section">
            <div class="settings-section-title">Project</div>
            <div class="row" style="gap:8px; align-items:center; margin-bottom:8px;">
              <label class="small" for="settings-project-data-policy" style="min-width:84px;">Data policy</label>
              <select id="settings-project-data-policy" style="min-width:170px;">
                <option value="reference">Reference (lightweight)</option>
                <option value="embed">Embed data (portable)</option>
              </select>
              <label class="small" for="settings-project-embed-max" style="min-width:72px;">Embed max pts/layer</label>
              <input id="settings-project-embed-max" type="number" min="0" step="1000" style="width:110px;" />
            </div>
            <div class="row" style="gap:8px;">
              <button type="button" class="btn" id="settings-project-save">Save Project</button>
              <button type="button" class="btn" id="settings-project-save-bundle">Save Bundle (.zip)</button>
              <button type="button" class="btn" id="settings-project-load">Load Project</button>
              <button type="button" class="btn" id="settings-project-load-bundle">Load Bundle (.zip)</button>
              <button type="button" class="btn" id="settings-project-estimate-btn">Estimate Size</button>
              <input id="settings-project-file" type="file" accept=".json,.hydroproj,.hydroproj.json,application/json,.zip" style="display:none" />
              <input id="settings-project-relink-file" type="file" style="display:none" />
              <input id="settings-project-relink-folder" type="file" webkitdirectory directory multiple style="display:none" />
            </div>
            <div class="small" style="margin-top:6px;">Saves map/layout/tool state. Use Reference for small files, Embed for portable datasets.</div>
            <div id="settings-project-estimate" class="small" style="margin-top:4px;">Estimated size: n/a</div>
            <div id="settings-restore-report" style="margin-top:8px;"></div>
            <div class="row" style="gap:8px; margin-top:8px;">
              <button type="button" class="btn" id="settings-project-batch-relink">Batch Relink (Folder)</button>
            </div>
          </div>
          <div class="settings-section">
            <div class="settings-section-title">Performance</div>
            <div class="row" style="gap:8px; align-items:center; margin-bottom:6px;">
              <label class="small"><input id="settings-project-autosave-enabled" type="checkbox" /> Autosave project snapshot</label>
              <label class="small">Interval (sec)</label>
              <input id="settings-project-autosave-interval" type="number" min="15" step="15" style="width:80px;" />
              <button type="button" class="btn" id="settings-project-restore-autosave">Restore Last Autosave</button>
              <button type="button" class="btn" id="settings-project-clear-autosave">Clear Autosave</button>
            </div>
            <div class="small">Last autosave: <span id="settings-project-autosave-stamp">never</span></div>
          </div>
        </div>
      </div>
    `;
    rootEl.appendChild(settingsModalEl);
    settingsPanelEl = settingsModalEl.querySelector(".settings-modal");

    settingsModalEl.addEventListener("click", (e) => {
      if (e.target === settingsModalEl) {
        closeSettingsModal();
      }
    });
    settingsModalEl.querySelector("#settings-ui-theme")?.addEventListener("change", (e) => {
      applyUiTheme(String(e?.target?.value || "deep-blue"));
    });

    settingsModalEl.querySelector("#settings-close")?.addEventListener("click", closeSettingsModal);
    settingsModalEl.querySelector("#settings-project-save")?.addEventListener("click", saveProjectFile);
    settingsModalEl.querySelector("#settings-project-save-bundle")?.addEventListener("click", saveProjectBundleFile);
    settingsModalEl.querySelector("#settings-project-load")?.addEventListener("click", () => {
      settingsModalEl.querySelector("#settings-project-file")?.click();
    });
    settingsModalEl.querySelector("#settings-project-load-bundle")?.addEventListener("click", () => {
      settingsModalEl.querySelector("#settings-project-file")?.click();
    });
    settingsModalEl.querySelector("#settings-project-estimate-btn")?.addEventListener("click", updateProjectEstimate);
    settingsModalEl.querySelector("#settings-project-file")?.addEventListener("change", (e) => {
      const file = e.target?.files?.[0];
      if (file) loadProjectFile(file);
      e.target.value = "";
    });
    settingsModalEl.addEventListener("click", async (e) => {
      const relinkBtn = e.target?.closest?.("[data-project-relink]");
      if (!relinkBtn) return;
      const idx = Number(relinkBtn.getAttribute("data-project-relink"));
      if (!Number.isFinite(idx) || idx < 0 || idx >= relinkItems.length) return;
      pendingRelinkRef = relinkItems[idx];
      settingsModalEl.querySelector("#settings-project-relink-file")?.click();
    });
    settingsModalEl.querySelector("#settings-project-relink-file")?.addEventListener("change", async (e) => {
      const file = e.target?.files?.[0];
      if (!file || !pendingRelinkRef) {
        e.target.value = "";
        return;
      }
      const ref = pendingRelinkRef;
      pendingRelinkRef = null;
      const out = await runtimeRef?.tools?.resolveProjectReference?.(ref, file);
      if (!out?.ok) {
        toast(out?.message || "Relink failed");
        e.target.value = "";
        return;
      }
      if (lastRestoreReport) {
        lastRestoreReport.unresolved = (lastRestoreReport.unresolved || []).filter((u) => !(u.toolId === ref.toolId && u.referenceId === ref.referenceId));
        lastRestoreReport.restored = Number(lastRestoreReport.restored || 0) + Number(out.restored || 1);
      }
      renderRestoreReport(lastRestoreReport);
      events.emit("project:restore:report", lastRestoreReport);
      toast(out?.message || "Source relinked");
      e.target.value = "";
    });
    settingsModalEl.querySelector("#settings-project-batch-relink")?.addEventListener("click", () => {
      settingsModalEl.querySelector("#settings-project-relink-folder")?.click();
    });
    settingsModalEl.querySelector("#settings-project-relink-folder")?.addEventListener("change", async (e) => {
      const files = [...(e.target?.files || [])];
      if (!files.length) {
        e.target.value = "";
        return;
      }
      const byName = new Map(files.map((f) => [String(f.name || "").toLowerCase(), f]));
      let resolved = 0;
      for (const ref of relinkItems.slice()) {
        const expected = String(ref.expectedFileName || "").toLowerCase();
        if (!expected || !byName.has(expected)) continue;
        const file = byName.get(expected);
        const out = await runtimeRef?.tools?.resolveProjectReference?.(ref, file);
        if (out?.ok) {
          resolved += Number(out?.restored || 1);
          if (lastRestoreReport) {
            lastRestoreReport.unresolved = (lastRestoreReport.unresolved || []).filter((u) => !(u.toolId === ref.toolId && u.referenceId === ref.referenceId));
            lastRestoreReport.restored = Number(lastRestoreReport.restored || 0) + Number(out.restored || 1);
          }
        }
      }
      renderRestoreReport(lastRestoreReport);
      events.emit("project:restore:report", lastRestoreReport);
      toast(resolved ? `Batch relink restored ${resolved} source(s)` : "No filename matches found in folder");
      e.target.value = "";
    });
    const policySel = settingsModalEl.querySelector("#settings-project-data-policy");
    if (policySel) {
      policySel.value = localStorage.getItem(PROJECT_DATA_POLICY_KEY) || "reference";
      policySel.addEventListener("change", () => {
        localStorage.setItem(PROJECT_DATA_POLICY_KEY, policySel.value || "reference");
        updateProjectEstimate();
      });
    }
    const embedMaxEl = settingsModalEl.querySelector("#settings-project-embed-max");
    if (embedMaxEl) {
      embedMaxEl.value = localStorage.getItem(PROJECT_EMBED_MAX_SOUNDINGS_KEY) || "0";
      embedMaxEl.addEventListener("change", () => {
        localStorage.setItem(PROJECT_EMBED_MAX_SOUNDINGS_KEY, String(Math.max(0, Number(embedMaxEl.value || 0) || 0)));
        updateProjectEstimate();
      });
    }
    const autosaveEnabledEl = settingsModalEl.querySelector("#settings-project-autosave-enabled");
    const autosaveIntervalEl = settingsModalEl.querySelector("#settings-project-autosave-interval");
    const autosaveStampEl = settingsModalEl.querySelector("#settings-project-autosave-stamp");
    if (autosaveEnabledEl && autosaveIntervalEl) {
      autosaveEnabledEl.checked = localStorage.getItem(PROJECT_AUTOSAVE_ENABLED_KEY) === "1";
      autosaveIntervalEl.value = localStorage.getItem(PROJECT_AUTOSAVE_INTERVAL_KEY) || "90";
      autosaveStampEl.textContent = localStorage.getItem(PROJECT_AUTOSAVE_AT_KEY) || "never";
      autosaveEnabledEl.addEventListener("change", () => {
        localStorage.setItem(PROJECT_AUTOSAVE_ENABLED_KEY, autosaveEnabledEl.checked ? "1" : "0");
        configureAutosave();
      });
      autosaveIntervalEl.addEventListener("change", () => {
        localStorage.setItem(PROJECT_AUTOSAVE_INTERVAL_KEY, String(Math.max(15, Number(autosaveIntervalEl.value || 90) || 90)));
        configureAutosave();
      });
    }
    settingsModalEl.querySelector("#settings-project-restore-autosave")?.addEventListener("click", restoreAutosaveSnapshot);
    settingsModalEl.querySelector("#settings-project-clear-autosave")?.addEventListener("click", clearAutosaveSnapshot);

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") closeSettingsModal();
    });

    applyUiTheme(localStorage.getItem(UI_THEME_KEY) || "deep-blue");
    renderRestoreReport(lastRestoreReport);
    updateProjectEstimate();
  }

  function ensureSettingsButton() {
    if (topbarEl) {
      const existing = topbarEl.querySelector("#global-settings-btn");
      if (!existing) {
        const btn = document.createElement("button");
        btn.id = "global-settings-btn";
        btn.className = "btn";
        btn.type = "button";
        btn.textContent = "Settings";
        btn.addEventListener("click", openSettingsModal);
        topbarEl.prepend(btn);
      }
      return;
    }
    const header = rootEl.querySelector(".app-header");
    const brand = rootEl.querySelector(".brand");
    if (!header || !brand) return;
    let wrap = rootEl.querySelector(".brand-wrap");
    if (!wrap) {
      wrap = document.createElement("div");
      wrap.className = "brand-wrap";
      brand.parentElement?.insertBefore?.(wrap, brand);
      wrap.appendChild(brand);
    }
    if (wrap.querySelector("#global-settings-btn")) return;
    const btn = document.createElement("button");
    btn.id = "global-settings-btn";
    btn.className = "btn";
    btn.type = "button";
    btn.textContent = "Settings";
    btn.addEventListener("click", openSettingsModal);
    wrap.appendChild(btn);
  }

  function ensureSimulatorFullscreenHandoff() {
    const simLink = rootEl?.querySelector?.('.header-link[href="/simulator.html"]');
    if (!simLink || simLink.dataset.fullscreenHandoffBound === "1") return;
    simLink.dataset.fullscreenHandoffBound = "1";
    simLink.addEventListener("click", () => {
      if (!isFullscreenActive()) {
        try { sessionStorage.removeItem(FULLSCREEN_SIM_HANDOFF_KEY); } catch {}
        return;
      }
      try {
        sessionStorage.setItem(FULLSCREEN_SIM_HANDOFF_KEY, JSON.stringify({
          ts: Date.now(),
          from: window.location.pathname,
        }));
      } catch {}
    });
  }

  function isFullscreenActive() {
    return !!document.fullscreenElement;
  }

  function syncFullscreenUi() {
    const active = isFullscreenActive();
    if (fullscreenBtnEl) fullscreenBtnEl.textContent = active ? "Exit Fullscreen" : "Fullscreen";
    if (fullscreenExitBtnEl) {
      fullscreenExitBtnEl.setAttribute("aria-hidden", active ? "false" : "true");
      fullscreenExitBtnEl.tabIndex = active ? 0 : -1;
    }
    rootEl?.classList?.toggle("is-fullscreen", active);
  }

  async function toggleFullscreen() {
    try {
      if (!document.fullscreenEnabled) {
        toast("Fullscreen is not supported by this browser");
        return;
      }
      if (isFullscreenActive()) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
      syncFullscreenUi();
      dispatchMapResizePulse();
    } catch (error) {
      console.error("[UI] fullscreen toggle failed", error);
      toast("Fullscreen toggle failed");
    }
  }

  function ensureFullscreenButton() {
    const wrap = rootEl.querySelector(".brand-wrap");
    if (!wrap) return;
    if (!fullscreenBtnEl) {
      const btn = document.createElement("button");
      btn.id = "global-fullscreen-btn";
      btn.className = "btn";
      btn.type = "button";
      btn.textContent = "Fullscreen";
      btn.addEventListener("click", toggleFullscreen);
      wrap.appendChild(btn);
      fullscreenBtnEl = btn;
    }
    if (!fullscreenExitBtnEl) {
      const btn = document.createElement("button");
      btn.id = "global-fullscreen-exit-btn";
      btn.className = "fullscreen-exit-btn";
      btn.type = "button";
      btn.textContent = "Exit";
      btn.title = "Exit Fullscreen";
      btn.setAttribute("aria-label", "Exit Fullscreen");
      btn.setAttribute("aria-hidden", "true");
      btn.tabIndex = -1;
      btn.addEventListener("click", toggleFullscreen);
      rootEl.appendChild(btn);
      fullscreenExitBtnEl = btn;
    }
    if (!fullscreenBound) {
      document.addEventListener("fullscreenchange", syncFullscreenUi);
      fullscreenBound = true;
    }
    syncFullscreenUi();
  }

  function ensureFileDropOverlay() {
    if (fileDropOverlayEl) return fileDropOverlayEl;
    const el = document.createElement("div");
    el.className = "file-drop-overlay";
    el.setAttribute("aria-hidden", "true");
    el.innerHTML = `
      <div class="file-drop-overlay-card">
        <div class="file-drop-overlay-title">Drop files to import</div>
        <div class="file-drop-overlay-copy">KMZ, KML, GeoJSON, GPX, DXF, SHP ZIP</div>
      </div>
    `;
    rootEl.appendChild(el);
    fileDropOverlayEl = el;
    return el;
  }

  function setFileDropOverlayVisible(visible) {
    const el = ensureFileDropOverlay();
    el.setAttribute("aria-hidden", visible ? "false" : "true");
  }

  function isImportableDraggedFile(event) {
    const items = Array.from(event?.dataTransfer?.items || []);
    if (!items.length) return false;
    return items.some((item) => item.kind === "file");
  }

  function ensureGlobalFileDrop() {
    ensureFileDropOverlay();
    const reset = () => {
      fileDropDepth = 0;
      setFileDropOverlayVisible(false);
    };
    rootEl.addEventListener("dragenter", (e) => {
      if (!isImportableDraggedFile(e)) return;
      fileDropDepth += 1;
      setFileDropOverlayVisible(true);
    });
    rootEl.addEventListener("dragover", (e) => {
      if (!isImportableDraggedFile(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      setFileDropOverlayVisible(true);
    });
    rootEl.addEventListener("dragleave", (e) => {
      if (!isImportableDraggedFile(e)) return;
      fileDropDepth = Math.max(0, fileDropDepth - 1);
      if (fileDropDepth === 0 || e.target === rootEl) setFileDropOverlayVisible(false);
    });
    rootEl.addEventListener("drop", async (e) => {
      if (!isImportableDraggedFile(e)) return;
      e.preventDefault();
      reset();
      const files = Array.from(e.dataTransfer?.files || []);
      if (!files.length) return;
      try {
        const out = await runtimeRef?.tools?.importDroppedFiles?.(files);
        if (out?.handled) {
          toast(`Imported ${out.imported} dropped file${out.imported === 1 ? "" : "s"} into Linework`);
          return;
        }
        if (out?.pendingSiblingLookup) return;
      } catch (err) {
        console.error("[UI] dropped file import failed", err);
        toast(err?.message || "Dropped file import failed");
        return;
      }
      toast("Dropped files were not recognized for import");
    });
    window.addEventListener("dragend", reset);
    window.addEventListener("drop", (e) => {
      if (e.target === rootEl || rootEl.contains(e.target)) return;
      reset();
    });
  }

  function bindRuntime(runtime) {
    runtimeRef = runtime || null;
    configureAutosave();
  }

  function addTopbarButton({ id, label, onClick, primary = false }) {
    const btn = document.createElement("button");
    btn.className = `btn ${primary ? "primary" : ""}`;
    btn.id = id;
    btn.textContent = label;
    btn.addEventListener("click", onClick);
    topbarEl.appendChild(btn);
    return () => btn.remove();
  }

  function mountLeftPanel(html) {
    leftPanelEl.innerHTML = `<div class="panel-content">${html}</div>`;
    addResizer(leftPanelEl, "left", "--leftPanelW", LEFT_W_KEY);
  }

  function mountRightPanel(html) {
    rightPanelEl.innerHTML = `<div class="panel-content">${html}</div>`;
    addResizer(rightPanelEl, "right", "--rightPanelW", RIGHT_W_KEY);
  }

  function mountBottomPanel(html, options = {}) {
    const ownerId = String(options?.ownerId || "default");
    const title = String(options?.title || "Bottom Panel");
    const activate = options?.activate === true;
    const existing = bottomPanelEntries.get(ownerId);
    const bodyEl = existing?.bodyEl || document.createElement("div");
    bodyEl.className = "bottom-panel-view";
    bodyEl.innerHTML = String(html || "");
    bottomPanelEntries.set(ownerId, {
      id: ownerId,
      title,
      bodyEl,
      onShow: typeof options?.onShow === "function" ? options.onShow : existing?.onShow || null,
    });
    if (!activeBottomPanelId || !bottomPanelEntries.has(activeBottomPanelId) || activate) {
      activeBottomPanelId = ownerId;
    }
    renderBottomPanelHost();
  }

  function unmountBottomPanel(ownerId = "default") {
    const id = String(ownerId || "default");
    if (!bottomPanelEntries.has(id)) return;
    bottomPanelEntries.delete(id);
    if (activeBottomPanelId === id) {
      activeBottomPanelId = [...bottomPanelEntries.keys()].pop() || "";
    }
    renderBottomPanelHost();
  }

  function showBottomPanel(ownerId) {
    const id = String(ownerId || "");
    if (!id || !bottomPanelEntries.has(id)) return false;
    activeBottomPanelId = id;
    renderBottomPanelHost();
    return true;
  }

  function toast(message) {
    const el = document.createElement("div");
    el.className = "card";
    el.style.pointerEvents = "auto";
    el.textContent = message;
    hudEl.appendChild(el);
    setTimeout(() => el.remove(), 2600);
  }

  // ── File Browser Modal ───────────────────────────────────────────────────
  function ensureFileBrowser() {
    if (document.getElementById("fb-backdrop")) {
      return { open: document.getElementById("fb-backdrop")._fbOpen || (() => {}) };
    }

    const VM_ROOT = "/home/ubuntu/data";
    let activeTab = "vm";
    const vmNodes = new Map();   // path   → { items, loading, error }
    const s3Nodes = new Map();   // prefix → { items, loading, error }
    const vmExpanded = new Set();
    const s3Expanded = new Set();

    const backdrop = document.createElement("div");
    backdrop.id = "fb-backdrop";
    backdrop.setAttribute("aria-hidden", "true");
    backdrop.innerHTML = `
      <div id="fb-panel" role="dialog" aria-label="File Browser">
        <div class="fb-head">
          <span class="fb-title">File Browser</span>
          <div class="fb-tabs">
            <button class="fb-tab" data-fb-tab="vm">VM Disk</button>
            <button class="fb-tab" data-fb-tab="s3">S3 Storage</button>
          </div>
          <button class="fb-close-btn" title="Close">✕</button>
        </div>
        <div class="fb-tree" id="fb-tree"></div>
      </div>
    `;
    rootEl.appendChild(backdrop);
    const treeEl = backdrop.querySelector("#fb-tree");

    function fmtSize(b) {
      if (b == null) return "";
      const n = Number(b);
      if (!Number.isFinite(n)) return "";
      if (n >= 1e9) return `${(n / 1e9).toFixed(1)} GB`;
      if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
      if (n >= 1e3) return `${(n / 1e3).toFixed(1)} KB`;
      return `${n} B`;
    }

    function esc(v) { return escapeUiText(v); }
    function escA(v) { return String(v ?? "").replace(/"/g, "&quot;"); }

    function renderVmLevel(lines, dirPath, depth) {
      const node = vmNodes.get(dirPath);
      if (!node || !node.items) return;
      const pad = depth * 16;
      if (!node.items.length) {
        lines.push(`<div class="fb-row" style="padding-left:${pad + 18}px"><span class="fb-muted">(empty)</span></div>`);
        return;
      }
      for (const item of node.items) {
        if (item.type === "dir") {
          const expanded = vmExpanded.has(item.path);
          const child = vmNodes.get(item.path);
          const tog = child?.loading ? "…" : expanded ? "▼" : "▶";
          lines.push(`<div class="fb-row fb-dir" data-vm-path="${escA(item.path)}" style="padding-left:${pad}px"><span class="fb-toggle">${tog}</span><span class="fb-name">${esc(item.name)}/</span></div>`);
          if (expanded) {
            if (child?.error) lines.push(`<div class="fb-row" style="padding-left:${pad + 34}px"><span class="fb-err">${esc(child.error)}</span></div>`);
            else renderVmLevel(lines, item.path, depth + 1);
          }
        } else {
          lines.push(`<div class="fb-row fb-file" style="padding-left:${pad + 18}px"><span class="fb-name">${esc(item.name)}</span><span class="fb-size">${fmtSize(item.size)}</span></div>`);
        }
      }
    }

    function renderS3Level(lines, prefix, depth) {
      const node = s3Nodes.get(prefix);
      if (!node || !node.items) return;
      const pad = depth * 16;
      const { folders = [], files = [], truncated } = node.items;
      if (!folders.length && !files.length) {
        lines.push(`<div class="fb-row" style="padding-left:${pad + 18}px"><span class="fb-muted">(empty)</span></div>`);
        return;
      }
      for (const folder of folders) {
        const expanded = s3Expanded.has(folder.prefix);
        const child = s3Nodes.get(folder.prefix);
        const tog = child?.loading ? "…" : expanded ? "▼" : "▶";
        lines.push(`<div class="fb-row fb-dir" data-s3-prefix="${escA(folder.prefix)}" style="padding-left:${pad}px"><span class="fb-toggle">${tog}</span><span class="fb-name">${esc(folder.name)}/</span></div>`);
        if (expanded) {
          if (child?.error) lines.push(`<div class="fb-row" style="padding-left:${pad + 34}px"><span class="fb-err">${esc(child.error)}</span></div>`);
          else renderS3Level(lines, folder.prefix, depth + 1);
        }
      }
      for (const file of files) {
        lines.push(`<div class="fb-row fb-file" style="padding-left:${pad + 18}px"><span class="fb-name">${esc(file.name)}</span><span class="fb-size">${fmtSize(file.size)}</span></div>`);
      }
      if (truncated) lines.push(`<div class="fb-row" style="padding-left:${pad + 18}px"><span class="fb-muted">…more items</span></div>`);
    }

    function render() {
      backdrop.querySelectorAll(".fb-tab").forEach(btn => btn.classList.toggle("is-active", btn.dataset.fbTab === activeTab));
      const lines = [];
      if (activeTab === "vm") {
        const root = vmNodes.get(VM_ROOT);
        if (!root || root.loading) lines.push(`<div class="fb-row fb-loading"><span class="fb-muted">Loading…</span></div>`);
        else if (root.error) lines.push(`<div class="fb-row"><span class="fb-err">${esc(root.error)}</span></div>`);
        else renderVmLevel(lines, VM_ROOT, 0);
      } else {
        const root = s3Nodes.get("");
        if (!root || root.loading) lines.push(`<div class="fb-row fb-loading"><span class="fb-muted">Loading…</span></div>`);
        else if (root.error) lines.push(`<div class="fb-row"><span class="fb-err">${esc(root.error)}</span></div>`);
        else renderS3Level(lines, "", 0);
      }
      treeEl.innerHTML = lines.join("");
    }

    async function loadVm(dirPath) {
      if (vmNodes.get(dirPath)?.items) return;
      vmNodes.set(dirPath, { loading: true });
      render();
      try {
        const data = await fetch(`/api/system/files/vm?path=${encodeURIComponent(dirPath)}`).then(r => r.json()).catch(() => ({ ok: false, error: "Network error" }));
        if (!data.ok) throw new Error(data.error || "Error");
        vmNodes.set(dirPath, { items: data.items });
      } catch (err) {
        vmNodes.set(dirPath, { error: String(err?.message || err) });
      }
      render();
    }

    async function loadS3(prefix) {
      if (s3Nodes.get(prefix)?.items) return;
      s3Nodes.set(prefix, { loading: true });
      render();
      try {
        const data = await fetch(`/api/system/files/s3?prefix=${encodeURIComponent(prefix)}`).then(r => r.json()).catch(() => ({ ok: false, error: "Network error" }));
        if (!data.ok) throw new Error(data.error || "Error");
        s3Nodes.set(prefix, { items: { folders: data.folders, files: data.files, truncated: data.truncated } });
      } catch (err) {
        s3Nodes.set(prefix, { error: String(err?.message || err) });
      }
      render();
    }

    treeEl.addEventListener("click", e => {
      const row = e.target.closest("[data-vm-path],[data-s3-prefix]");
      if (!row) return;
      if (row.hasAttribute("data-vm-path")) {
        const p = row.getAttribute("data-vm-path");
        if (vmExpanded.has(p)) vmExpanded.delete(p);
        else { vmExpanded.add(p); loadVm(p); }
        render();
      } else {
        const p = row.getAttribute("data-s3-prefix");
        if (s3Expanded.has(p)) s3Expanded.delete(p);
        else { s3Expanded.add(p); loadS3(p); }
        render();
      }
    });

    backdrop.addEventListener("click", e => {
      if (e.target.closest(".fb-close-btn") || e.target === backdrop) { closeFb(); return; }
      const tab = e.target.closest(".fb-tab");
      if (tab?.dataset?.fbTab) {
        activeTab = tab.dataset.fbTab;
        render();
        if (activeTab === "vm") loadVm(VM_ROOT);
        else loadS3("");
      }
    });

    document.addEventListener("keydown", e => {
      if (e.key === "Escape" && backdrop.getAttribute("aria-hidden") === "false") closeFb();
    });

    function closeFb() { backdrop.setAttribute("aria-hidden", "true"); }

    function openFb(tab = "vm") {
      activeTab = tab;
      backdrop.setAttribute("aria-hidden", "false");
      render();
      if (activeTab === "vm") loadVm(VM_ROOT);
      else loadS3("");
    }

    backdrop._fbOpen = openFb;
    return { open: openFb };
  }

  // ── System Status Bar ────────────────────────────────────────────────────
  function ensureSysBar() {
    if (document.getElementById("sys-bar")) return;
    const bar = document.createElement("div");
    bar.id = "sys-bar";

    const tasks = new Map();
    const { open: openFb } = ensureFileBrowser();

    let storageData = null;
    let cpuData = null;
    let s3Data = null;
    let hoverDepthText = null;
    let netSpeedMbps = null;
    let metricsTimer = null;
    let speedTimer = null;

    // Boot phase — timer-animated bar (always visible) + real event messages
    // The animation runs independently of whether any tool fires events.
    // Real events update the message text; slip-inventory idle closes the bar early.
    const BOOT_SOURCES = ["slip-inventory"];
    let bootDone = false;
    let bootPct = 0;
    let bootMsg = "Starting up…";
    const BOOT_ANIM_MS = 2500; // how long 0→90% takes
    const bootStart = performance.now();
    function tickBoot() {
      if (bootDone) return;
      const frac = Math.min(1, (performance.now() - bootStart) / BOOT_ANIM_MS);
      bootPct = Math.round(90 * frac);
      render();
      if (frac < 1) setTimeout(tickBoot, 80);
      // At frac=1 (90%) we hold until a boot source goes idle or the 6 s safety fires
    }
    setTimeout(tickBoot, 0);
    // Safety: force-complete after 6 s
    setTimeout(() => {
      if (!bootDone) { bootPct = 100; render(); setTimeout(() => { bootDone = true; render(); }, 500); }
    }, 6000);

    function fmtPct(p) {
      const n = Number(p);
      return Number.isFinite(n) ? `${Math.round(n)}%` : "—";
    }

    function miniBar(pct) {
      const n = Number(pct);
      if (!Number.isFinite(n)) return "";
      const p = Math.max(0, Math.min(100, n));
      const color = p >= 90 ? "#ef4444" : p >= 75 ? "#f59e0b" : "var(--accent)";
      return `<span class="sys-mini-bar"><span class="sys-mini-fill" style="width:${p}%;background:${color};"></span></span>`;
    }

    function render() {
      const running = [...tasks.values()].filter(t => t.status === "running");
      bar.classList.toggle("sys-bar--active", running.length > 0 || !bootDone);

      let taskHtml;
      if (!bootDone) {
        taskHtml = [
          `<span class="sys-icon sys-pulse" style="color:var(--accent);">◉</span>`,
          `<span class="sys-label">Loading environment</span>`,
          `<div class="sys-task-bar sys-boot-bar"><div class="sys-task-fill" style="width:${Math.max(2, bootPct)}%;"></div></div>`,
          `<span class="sys-val">${Math.round(bootPct)}%</span>`,
          bootMsg ? `<span class="sys-msg">${bootMsg}</span>` : "",
        ].join("");
      } else if (!running.length) {
        taskHtml = `<span class="sys-icon" style="opacity:.35;">◉</span><span class="sys-muted">Idle</span>`;
      } else {
        const t = running[0];
        const p = Number.isFinite(Number(t.progress)) ? Math.max(0, Math.min(100, Number(t.progress))) : null;
        const progressHtml = p !== null
          ? `<div class="sys-task-bar"><div class="sys-task-fill" style="width:${Math.max(2, p)}%;"></div></div><span class="sys-val">${Math.round(p)}%</span>`
          : "";
        const extra = running.length > 1 ? `<span class="sys-muted">+${running.length - 1} more</span>` : "";
        taskHtml = [
          `<span class="sys-icon sys-pulse" style="color:var(--accent);">◉</span>`,
          `<span class="sys-label">${t.label}</span>`,
          progressHtml,
          `<span class="sys-msg">${t.message || ""}</span>`,
          extra,
        ].join("");
      }

      const diskPct = storageData?.disk?.usedPercent ?? null;
      const cores = cpuData?.cpu?.cores || [];
      const cpuOverall = cpuData?.cpu?.overall ?? null;
      const memPct = cpuData?.memory?.usedPercent ?? null;
      const s3Bytes = s3Data?.bytes ?? null;
      const hasMetrics = diskPct != null || cpuOverall != null || memPct != null || s3Bytes != null || hoverDepthText != null || netSpeedMbps != null;
      let metricsHtml = "";
      if (hasMetrics) {
        const parts = [];
        if (diskPct != null) parts.push(`<span class="sys-metric-lbl sys-metric-btn" data-fb-open="vm">VM Disk</span>${miniBar(diskPct)}<span class="sys-metric-val">${fmtPct(diskPct)}</span>`);
        if (s3Bytes != null) {
          const s3Fmt = s3Bytes >= 1e9 ? `${(s3Bytes / 1e9).toFixed(1)} GB`
            : s3Bytes >= 1e6 ? `${(s3Bytes / 1e6).toFixed(1)} MB`
            : `${(s3Bytes / 1e3).toFixed(0)} KB`;
          parts.push(`<span class="sys-metric-lbl sys-metric-btn" data-fb-open="s3">S3 Storage</span><span class="sys-metric-val">${s3Fmt}</span>`);
        }
        if (cores.length > 0) {
          const coreHtml = cores.map(c =>
            `<span class="sys-metric-lbl">${String(c.name || "").replace("cpu", "C")}</span>${miniBar(c.usedPercent)}<span class="sys-metric-val">${fmtPct(c.usedPercent)}</span>`
          ).join('<span class="sys-core-gap"></span>');
          parts.push(coreHtml);
        } else if (cpuOverall != null) {
          parts.push(`<span class="sys-metric-lbl">CPU</span>${miniBar(cpuOverall)}<span class="sys-metric-val">${fmtPct(cpuOverall)}</span>`);
        }
        if (memPct != null) parts.push(`<span class="sys-metric-lbl">RAM</span>${miniBar(memPct)}<span class="sys-metric-val">${fmtPct(memPct)}</span>`);
        if (netSpeedMbps != null) {
          const spd = netSpeedMbps;
          const spdFmt = spd >= 10 ? `${Math.round(spd)} Mbps` : `${spd.toFixed(1)} Mbps`;
          const spdColor = spd < 1 ? "#ef4444" : spd < 5 ? "#f59e0b" : "var(--accent)";
          const spdBar = miniBar(Math.min(100, (spd / 100) * 100));
          parts.push(`<span class="sys-metric-lbl">Net</span>${spdBar}<span class="sys-metric-val" style="color:${spdColor};">${spdFmt}</span>`);
        }
        if (hoverDepthText != null) parts.push(`<span class="sys-metric-lbl">Depth</span><span class="sys-metric-val">${hoverDepthText}</span>`);
        metricsHtml = `<div class="sys-metrics">${parts.join('<span class="sys-sep">|</span>')}</div>`;
      }

      const ctx = runtimeRef?.services?.context;
      const activeProject = ctx?.getActiveProject?.();
      const activeClient = ctx?.getActiveClient?.();
      const chipLabel = activeProject
        ? `${escapeUiText(activeClient?.name || "—")} › ${escapeUiText(activeProject.name)} · ${escapeUiText(activeProject.horizontalCRS)}`
        : "No Project";
      const chipColor = activeProject ? "rgba(148,163,184,.65)" : "rgba(251,191,36,.75)";
      const contextChip = `<button data-sys-action="context" title="Project Context" style="flex-shrink:0;background:none;border:none;cursor:pointer;font-size:10px;color:${chipColor};padding:0 10px 0 4px;line-height:1;white-space:nowrap;">◈ ${chipLabel}</button>`;
      bar.innerHTML = `<div class="sys-content">${contextChip}<div class="sys-tasks">${taskHtml}</div>${metricsHtml}<button data-sys-action="changelog" title="Agent Change Log" style="margin-left:auto;flex-shrink:0;background:none;border:none;cursor:pointer;font-size:10px;color:rgba(148,163,184,0.55);padding:0 4px 0 8px;line-height:1;white-space:nowrap;" >Change Log</button><a href="/roadmap.html" target="_blank" title="Project Roadmap" style="flex-shrink:0;background:none;border:none;cursor:pointer;font-size:10px;color:rgba(148,163,184,0.55);padding:0 4px;line-height:1;white-space:nowrap;text-decoration:none;display:flex;align-items:center;">Roadmap</a><button data-sys-action="log" title="Activity Log" style="flex-shrink:0;background:none;border:none;cursor:pointer;font-size:10px;color:rgba(148,163,184,0.55);padding:0 8px 0 4px;line-height:1;white-space:nowrap;" >◎ Log</button></div>`;
    }

    async function fetchMetrics() {
      try {
        const [sRes, cRes, s3Res] = await Promise.all([
          fetch("/api/system/storage").then(r => r.json()).catch(() => null),
          fetch("/api/system/cpu").then(r => r.json()).catch(() => null),
          fetch("/api/system/s3").then(r => r.json()).catch(() => null),
        ]);
        if (sRes && sRes.ok !== false) storageData = sRes;
        if (cRes && cRes.ok !== false) cpuData = cRes;
        if (s3Res && s3Res.ok !== false) s3Data = s3Res;
        render();
      } catch {}
    }

    async function measureSpeed() {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 6000);
      let received = 0;
      const t0 = performance.now();
      try {
        const r = await fetch("/api/system/speedtest", { cache: "no-store", signal: controller.signal });
        if (!r.ok) { clearTimeout(timeout); return; }
        const reader = r.body.getReader();
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          received += value.byteLength;
        }
      } catch (e) {
        if (e?.name !== "AbortError") { clearTimeout(timeout); return; }
        // AbortError expected — timeout fired mid-stream; compute from bytes received so far
      } finally {
        clearTimeout(timeout);
      }
      const elapsed = performance.now() - t0;
      if (received < 50_000 || elapsed < 100) return; // not enough data for a reliable reading
      netSpeedMbps = Math.round(((received * 8) / (elapsed / 1000)) / 1_000_000 * 10) / 10;
      render();
    }

    events.on("depth:hover", ({ text }) => { hoverDepthText = text ?? null; render(); });

    events.on("task:status", ({ source, label, message, progress, status: st }) => {
      if (!source) return;
      if (!bootDone) {
        // Update the displayed message from real events
        if (message) bootMsg = message;
        // Slip-inventory idle = real work is done; snap to 100% and close
        if (st === "idle" && BOOT_SOURCES.includes(source)) {
          bootPct = 100;
          render();
          setTimeout(() => { bootDone = true; render(); }, 500);
        }
      }
      if (st === "idle") tasks.delete(source);
      else tasks.set(source, { label: label || source, message: message || "", progress, status: st });
      render();
    });

    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) { fetchMetrics(); measureSpeed(); }
    });

    // ── Activity log panel ───────────────────────────────────────────────────
    let logPanel = null;
    let logOpen = false;
    let changeLogPanel = null;

    function fmtLogTs(iso) {
      try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" });
      } catch { return iso || ""; }
    }

    function openLogPanel() {
      if (logPanel) { logPanel.querySelector("#sys-log-close")?.click(); return; }
      logOpen = true;
      logPanel = document.createElement("div");
      logPanel.id = "sys-log-panel";
      Object.assign(logPanel.style, {
        position: "fixed", bottom: "32px", right: "0", width: "520px", maxHeight: "340px",
        background: "var(--panel-bg, #0f172a)", border: "1px solid rgba(148,163,184,0.15)",
        borderRadius: "8px 0 0 8px", overflow: "hidden", display: "flex", flexDirection: "column",
        zIndex: "9000", boxShadow: "0 -4px 24px rgba(0,0,0,0.5)",
      });
      logPanel.innerHTML = `
        <div style="display:flex;align-items:center;gap:8px;padding:7px 12px;border-bottom:1px solid rgba(148,163,184,0.12);flex-shrink:0;">
          <span style="font-size:11px;font-weight:600;color:var(--text,#e2e8f0);flex:1;">Activity Log</span>
          <span id="sys-log-status" style="font-size:9px;color:var(--muted,#64748b);">Loading…</span>
          <button id="sys-log-close" style="background:none;border:none;cursor:pointer;color:var(--muted,#64748b);font-size:13px;padding:0 2px;line-height:1;">✕</button>
        </div>
        <div id="sys-log-entries" style="overflow-y:auto;flex:1;font-size:10px;"></div>`;
      document.body.appendChild(logPanel);
      let logRefreshTimer = null;
      const closeLogPanel = () => {
        clearInterval(logRefreshTimer);
        logRefreshTimer = null;
        logPanel?.remove();
        logPanel = null;
        logOpen = false;
      };
      logPanel.querySelector("#sys-log-close").addEventListener("click", closeLogPanel);

      async function loadLog() {
        const statusEl = logPanel?.querySelector("#sys-log-status");
        const listEl = logPanel?.querySelector("#sys-log-entries");
        if (!listEl) return;
        try {
          const r = await fetch("/api/activity/log?limit=100");
          const d = await r.json();
          if (!logPanel) return;
          const scrolledToTop = listEl.scrollTop < 4;
          const prevScroll = listEl.scrollTop;
          if (statusEl) statusEl.textContent = `${(d.entries || []).length} entries · auto-refresh`;
          if (!d.entries?.length) {
            listEl.innerHTML = `<div style="padding:16px;color:var(--muted,#64748b);text-align:center;">No activity recorded yet.</div>`;
            return;
          }
          listEl.innerHTML = d.entries.map(e => {
            const det = e.details ? Object.entries(e.details).filter(([,v]) => v != null).map(([k,v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ") : "";
            return `<div style="display:flex;gap:8px;align-items:baseline;padding:5px 12px;border-bottom:1px solid rgba(148,163,184,0.07);">
              <span style="font-size:9px;color:var(--muted,#64748b);white-space:nowrap;font-family:monospace;flex-shrink:0;">${fmtLogTs(e.ts)}</span>
              <span style="font-size:9px;color:#38bdf8;white-space:nowrap;flex-shrink:0;">${e.user_email || "system"}</span>
              <span style="color:var(--text,#e2e8f0);white-space:nowrap;">${e.action}</span>
              ${det ? `<span style="color:var(--muted,#64748b);font-size:9px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${det.replaceAll('"',"&quot;")}">${det}</span>` : ""}
            </div>`;
          }).join("");
          listEl.scrollTop = scrolledToTop ? 0 : prevScroll;
        } catch { if (logPanel?.querySelector("#sys-log-status")) logPanel.querySelector("#sys-log-status").textContent = "Failed to load"; }
      }
      loadLog();
      logRefreshTimer = setInterval(() => { if (logPanel) loadLog(); else clearInterval(logRefreshTimer); }, 5000);
    }


    // ── Context Panel (Client / Project / EPSG) ───────────────────────────
    let contextPanel = null;

    function closeContextPanel() {
      contextPanel?.remove();
      contextPanel = null;
    }

    function openContextPanel() {
      if (contextPanel) { closeContextPanel(); return; }
      const ctx = runtimeRef?.services?.context;
      if (!ctx) return;

      let panelClientId = ctx.getActiveClient()?.id || ctx.getClients()[0]?.id || null;
      let panelProjectId = ctx.getActiveProject()?.id || null;
      let panelMode = "view";
      let pickingLocation = false;
      let pendingLocation = null; // { lat, lng } captured during pick

      contextPanel = document.createElement("div");
      contextPanel.id = "sys-context-panel";
      contextPanel.style.cssText = "position:fixed;bottom:22px;left:0;z-index:10000;width:min(760px,100vw);height:360px;background:rgba(10,15,28,.98);border:1px solid rgba(148,163,184,.18);border-radius:0 8px 0 0;box-shadow:0 -8px 32px rgba(0,0,0,.65);display:flex;flex-direction:column;overflow:hidden;font-family:inherit;";

      function esc(v) { return escapeUiText(v); }

      function crsOptions(selected) {
        return (runtimeRef?.services?.projections?.listCRS?.() || [])
          .map(c => `<option value="${c.code}"${c.code === selected ? " selected" : ""}>${c.code} — ${esc(c.name)}</option>`)
          .join("");
      }

      function hzUnitOptions(selected) {
        return [
          { v: "ftUS", l: "US Feet" },
          { v: "ftInt", l: "International Feet" },
          { v: "m", l: "Meters" },
        ].map(o => `<option value="${o.v}"${o.v === selected ? " selected" : ""}>${o.l}</option>`).join("");
      }

      function zUnitOptions(selected) {
        return [
          { v: "ft", l: "Feet" },
          { v: "m", l: "Meters" },
        ].map(o => `<option value="${o.v}"${o.v === selected ? " selected" : ""}>${o.l}</option>`).join("");
      }

      function startLocationPick() {
        const mapInst = runtimeRef?.map?.instance;
        if (!mapInst) return;
        pickingLocation = true;
        renderPanel();
        mapInst.once("click", (e) => {
          const zoom = mapInst.getZoom();
          pendingLocation = { lat: Number(e.latlng.lat.toFixed(6)), lng: Number(e.latlng.lng.toFixed(6)), zoom };
          pickingLocation = false;
          renderPanel();
        });
      }

      function renderPanel() {
        const clients = ctx.getClients();
        const projects = panelClientId ? ctx.getProjects(panelClientId) : [];
        const catalogStatus = ctx.getCatalogStatus?.() || {};
        const activeProject = ctx.getActiveProject();
        const activeClient = ctx.getActiveClient();
        const viewProject = panelProjectId ? projects.find(p => p.id === panelProjectId) || null : null;
        const loc = pendingLocation || viewProject?.location || null;

        const clientsHtml = [
          ...clients.map(c => `
            <div data-client-id="${c.id}" style="padding:5px 8px;border-radius:4px;cursor:pointer;font-size:11px;display:flex;align-items:center;justify-content:space-between;background:${c.id === panelClientId ? "rgba(99,102,241,.18)" : "transparent"};color:${c.id === panelClientId ? "rgba(199,210,254,.9)" : "rgba(148,163,184,.65)"};">
              <span>${esc(c.name)}</span>
              ${activeClient?.id === c.id ? `<span style="font-size:8px;color:var(--accent,#38bdf8);">●</span>` : ""}
            </div>`),
          `<button data-ctx-action="new-client" style="width:100%;text-align:left;padding:5px 8px;border-radius:4px;background:none;border:none;cursor:pointer;font-size:11px;color:rgba(148,163,184,.35);">+ New Client</button>`,
        ].join("");

        const projectsHtml = panelClientId
          ? [
              ...projects.map(p => `
                <div data-project-id="${p.id}" style="padding:5px 8px;border-radius:4px;cursor:pointer;font-size:11px;display:flex;align-items:center;justify-content:space-between;background:${p.id === panelProjectId ? "rgba(99,102,241,.18)" : "transparent"};color:${p.id === panelProjectId ? "rgba(199,210,254,.9)" : "rgba(148,163,184,.65)"};">
                  <div><div>${esc(p.name)}</div><div style="font-size:9px;color:rgba(148,163,184,.35);margin-top:1px;">${esc(p.horizontalCRS)}</div></div>
                  ${activeProject?.id === p.id ? `<span style="font-size:8px;color:var(--accent,#38bdf8);">●</span>` : ""}
                </div>`),
              `<button data-ctx-action="new-project" style="width:100%;text-align:left;padding:5px 8px;border-radius:4px;background:none;border:none;cursor:pointer;font-size:11px;color:rgba(148,163,184,.35);">+ New Project</button>`,
            ].join("")
          : `<div style="padding:8px;font-size:11px;color:rgba(148,163,184,.3);">Select a client</div>`;

        const fieldControlStyle = "width:100%;min-width:0;height:24px;font-size:11px;padding:2px 8px;border-radius:7px;";
        const settingsWrap = (title, rows, actions = "") => `
          <div style="padding:10px 12px;min-width:0;overflow-x:hidden;">
            <div style="font-size:9px;color:rgba(148,163,184,.35);text-transform:uppercase;letter-spacing:.06em;margin-bottom:8px;">${title}</div>
            <div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px 10px;align-items:end;min-width:0;">
              ${rows}
            </div>
            ${actions}
          </div>`;
        const settingCell = (label, content, wide = false) => `
          <label style="${wide ? "grid-column:1/-1;" : ""}display:grid;grid-template-columns:78px minmax(0,1fr);gap:8px;align-items:center;min-width:0;border-bottom:1px solid rgba(148,163,184,.07);padding:3px 0;">
            <span style="font-size:10px;color:rgba(226,232,240,.82);white-space:nowrap;">${label}</span>
            <span style="min-width:0;">${content}</span>
          </label>`;

        let settingsHtml;
        if (panelMode === "new-client") {
          settingsHtml = settingsWrap(
            "New Client",
            settingCell("Name", `<input id="ctx-new-client-name" type="text" placeholder="Client name" style="${fieldControlStyle}" />`, true),
            `<div style="margin-top:10px;display:flex;gap:8px;justify-content:flex-end;">
                <button data-ctx-action="save-client" class="btn primary">Create Client</button>
                <button data-ctx-action="cancel-form" class="btn">Cancel</button>
              </div>`
          );
        } else if (panelMode === "new-project") {
          settingsHtml = settingsWrap(
            "New Project",
            `
              ${settingCell("Name", `<input id="ctx-new-proj-name" type="text" placeholder="Project name" style="${fieldControlStyle}" />`, true)}
              ${settingCell("Horiz CRS", `<select id="ctx-new-proj-crs" style="${fieldControlStyle}">${crsOptions("EPSG:4326")}</select>`)}
              ${settingCell("Vert Datum", `<input id="ctx-new-proj-vdatum" type="text" value="MSL" style="${fieldControlStyle}" />`)}
              ${settingCell("HZ Units", `<select id="ctx-new-proj-hunits" style="${fieldControlStyle}">${hzUnitOptions("m")}</select>`)}
              ${settingCell("Z Units", `<select id="ctx-new-proj-zunits" style="${fieldControlStyle}">${zUnitOptions("m")}</select>`)}
            `,
            `<div style="margin-top:10px;display:flex;gap:8px;justify-content:flex-end;">
                <button data-ctx-action="save-project" class="btn primary">Create Project</button>
                <button data-ctx-action="cancel-form" class="btn">Cancel</button>
              </div>`
          );
        } else if (viewProject) {
          const isActive = activeProject?.id === viewProject.id;
          const pickBtn = pickingLocation
            ? `<button data-ctx-action="cancel-pick" class="btn" style="font-size:10px;color:#f59e0b;">Click map… (cancel)</button>`
            : `<button data-ctx-action="pick-location" class="btn" style="font-size:10px;">Pick</button>`;
          const locDisplay = loc
            ? `<span style="font-size:10px;color:rgba(148,163,184,.62);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)}</span>`
            : `<span style="font-size:10px;color:rgba(148,163,184,.3);">not set</span>`;
          settingsHtml = settingsWrap(
            "Project Settings",
            `
              ${settingCell("Name", `<input id="ctx-edit-name" type="text" value="${esc(viewProject.name)}" style="${fieldControlStyle}" />`, true)}
              ${settingCell("Horiz CRS", `<select id="ctx-edit-crs" style="${fieldControlStyle}">${crsOptions(viewProject.horizontalCRS)}</select>`)}
              ${settingCell("Vert Datum", `<input id="ctx-edit-vdatum" type="text" value="${esc(viewProject.verticalDatum || "MSL")}" style="${fieldControlStyle}" />`)}
              ${settingCell("HZ Units", `<select id="ctx-edit-hunits" style="${fieldControlStyle}">${hzUnitOptions(viewProject.horizontalUnits || "m")}</select>`)}
              ${settingCell("Z Units", `<select id="ctx-edit-zunits" style="${fieldControlStyle}">${zUnitOptions(viewProject.verticalUnits || "m")}</select>`)}
              ${settingCell("Location", `<div style="display:flex;align-items:center;gap:6px;min-width:0;">${locDisplay}${pickBtn}</div>`, true)}
            `,
            `<div style="margin-top:10px;display:flex;gap:8px;flex-wrap:wrap;align-items:center;justify-content:flex-end;">
                ${isActive
                  ? `<span style="font-size:10px;color:var(--accent,#38bdf8);margin-right:auto;">● Active</span>`
                  : `<button data-ctx-action="activate-project" class="btn primary">Set Active</button>`}
                <button data-ctx-action="update-project" class="btn">Save</button>
                <button data-ctx-action="delete-project" class="btn" style="color:#fca5a5;">Delete</button>
              </div>`
          );
        } else {
          settingsHtml = `<div style="padding:16px;font-size:11px;color:rgba(148,163,184,.3);">Select a project</div>`;
        }

        const colHdr = (label) => `<div style="font-size:9px;color:rgba(148,163,184,.3);text-transform:uppercase;letter-spacing:.06em;padding:4px 8px 6px;">${label}</div>`;
        contextPanel.innerHTML = `
          <div style="display:flex;align-items:center;padding:7px 12px;border-bottom:1px solid rgba(148,163,184,.1);flex-shrink:0;">
            <span style="font-size:10px;font-weight:600;color:rgba(148,163,184,.5);letter-spacing:.05em;text-transform:uppercase;">Project Context</span>
            <button data-ctx-action="refresh-catalog" title="Refresh shared project catalog" style="margin-left:10px;background:none;border:none;cursor:pointer;font-size:10px;color:rgba(148,163,184,.45);padding:0;">${catalogStatus.status === "loading" ? "Syncing…" : "Refresh"}</button>
            ${catalogStatus.status === "error" ? `<span style="margin-left:8px;font-size:10px;color:#fca5a5;" title="${esc(catalogStatus.error || "")}">Catalog sync failed</span>` : ""}
            ${pickingLocation ? `<span style="margin-left:12px;font-size:10px;color:#f59e0b;animation:pulse 1s infinite;">Click map to set location…</span>` : ""}
            <button id="sys-context-close" style="margin-left:auto;background:none;border:none;cursor:pointer;color:rgba(148,163,184,.45);font-size:15px;line-height:1;padding:0 2px;">×</button>
          </div>
          <div style="display:grid;grid-template-columns:150px 175px minmax(0,1fr);flex:1;min-height:0;overflow:hidden;">
            <div style="border-right:1px solid rgba(148,163,184,.08);overflow-y:auto;padding:6px;">${colHdr("Clients")}${clientsHtml}</div>
            <div style="border-right:1px solid rgba(148,163,184,.08);overflow-y:auto;padding:6px;">${colHdr("Projects")}${projectsHtml}</div>
            <div style="overflow-y:auto;overflow-x:hidden;min-width:0;">${settingsHtml}</div>
          </div>`;
      }

      renderPanel();

      contextPanel.addEventListener("click", async e => {
        if (e.target.closest("#sys-context-close")) { closeContextPanel(); return; }

        const clientEl = e.target.closest("[data-client-id]");
        if (clientEl) { panelClientId = clientEl.dataset.clientId; panelProjectId = null; panelMode = "view"; pendingLocation = null; renderPanel(); return; }

        const projectEl = e.target.closest("[data-project-id]");
        if (projectEl) { panelProjectId = projectEl.dataset.projectId; panelMode = "view"; pendingLocation = null; renderPanel(); return; }

        const btn = e.target.closest("[data-ctx-action]");
        if (!btn) return;
        const action = btn.dataset.ctxAction;

        if (action === "new-client") { panelMode = "new-client"; renderPanel(); return; }
        if (action === "new-project") { panelMode = "new-project"; renderPanel(); return; }
        if (action === "cancel-form") { panelMode = "view"; pendingLocation = null; renderPanel(); return; }
        if (action === "refresh-catalog") {
          await ctx.refreshFromServer?.();
          render();
          renderPanel();
          return;
        }

        if (action === "pick-location") { startLocationPick(); return; }
        if (action === "cancel-pick") {
          pickingLocation = false;
          runtimeRef?.map?.instance?.off("click");
          renderPanel();
          return;
        }

        if (action === "save-client") {
          const name = contextPanel.querySelector("#ctx-new-client-name")?.value?.trim();
          if (!name) return;
          try {
            const client = await ctx.createClient({ name });
            panelClientId = client.id;
            panelMode = "view";
            renderPanel();
          } catch (err) {
            runtimeRef?.ui?.toast?.(`Create client failed: ${String(err?.message || err)}`);
            renderPanel();
          }
          return;
        }

        if (action === "save-project") {
          const name = contextPanel.querySelector("#ctx-new-proj-name")?.value?.trim();
          const horizontalCRS = contextPanel.querySelector("#ctx-new-proj-crs")?.value || "EPSG:4326";
          const horizontalUnits = contextPanel.querySelector("#ctx-new-proj-hunits")?.value || "m";
          const verticalDatum = contextPanel.querySelector("#ctx-new-proj-vdatum")?.value?.trim() || "MSL";
          const verticalUnits = contextPanel.querySelector("#ctx-new-proj-zunits")?.value || "m";
          if (!name || !panelClientId) return;
          try {
            const project = await ctx.createProject({ clientId: panelClientId, name, horizontalCRS, horizontalUnits, verticalDatum, verticalUnits });
            panelProjectId = project.id;
            panelMode = "view";
            pendingLocation = null;
            render();
            renderPanel();
          } catch (err) {
            runtimeRef?.ui?.toast?.(`Create project failed: ${String(err?.message || err)}`);
            renderPanel();
          }
          return;
        }

        if (action === "activate-project") {
          if (panelProjectId) {
            if (pendingLocation) {
              ctx.updateProject(panelProjectId, { location: pendingLocation });
              pendingLocation = null;
            }
            ctx.setActiveProject(panelProjectId);
            render();
            renderPanel();
          }
          return;
        }

        if (action === "update-project") {
          const name = contextPanel.querySelector("#ctx-edit-name")?.value?.trim();
          const horizontalCRS = contextPanel.querySelector("#ctx-edit-crs")?.value || "EPSG:4326";
          const horizontalUnits = contextPanel.querySelector("#ctx-edit-hunits")?.value || "m";
          const verticalDatum = contextPanel.querySelector("#ctx-edit-vdatum")?.value?.trim() || "MSL";
          const verticalUnits = contextPanel.querySelector("#ctx-edit-zunits")?.value || "m";
          if (!panelProjectId || !name) return;
          const updates = { name, horizontalCRS, horizontalUnits, verticalDatum, verticalUnits };
          if (pendingLocation) { updates.location = pendingLocation; pendingLocation = null; }
          ctx.updateProject(panelProjectId, updates);
          render();
          renderPanel();
          return;
        }

        if (action === "delete-project") {
          if (!panelProjectId || !confirm("Delete this project?")) return;
          ctx.deleteProject(panelProjectId);
          panelProjectId = null;
          pendingLocation = null;
          render();
          renderPanel();
          return;
        }
      });

      document.body.appendChild(contextPanel);
    }

    // Re-render chip and zoom map when project context changes
    events.on("context:changed", (data) => {
      render();
      const loc = data?.project?.location;
      if (loc && Number.isFinite(loc.lat) && Number.isFinite(loc.lng)) {
        try { runtimeRef?.map?.instance?.setView([loc.lat, loc.lng], loc.zoom || 16, { animate: true }); } catch {}
      }
    });

    events.on("context:catalog:changed", () => {
      render();
      if (contextPanel) {
        try {
          const activeClientId = runtimeRef?.services?.context?.getActiveClient?.()?.id || runtimeRef?.services?.context?.getClients?.()?.[0]?.id || null;
          const activeProjectId = runtimeRef?.services?.context?.getActiveProject?.()?.id || null;
          contextPanel.remove();
          contextPanel = null;
          openContextPanel();
          const clientEl = activeClientId ? contextPanel?.querySelector?.(`[data-client-id="${CSS.escape(activeClientId)}"]`) : null;
          const projectEl = activeProjectId ? contextPanel?.querySelector?.(`[data-project-id="${CSS.escape(activeProjectId)}"]`) : null;
          clientEl?.scrollIntoView?.({ block: "nearest" });
          projectEl?.scrollIntoView?.({ block: "nearest" });
        } catch {}
      }
    });

    events.on("context:catalog:error", () => {
      render();
    });

    function formatChangeLogMarkdown(text) {
      const lines = String(text || "").split(/\r?\n/);
      return lines.map((line) => {
        if (/^# /.test(line)) return `<div style="font-size:15px;font-weight:700;color:var(--text,#e2e8f0);margin:0 0 8px;">${escapeUiText(line.replace(/^# /, ""))}</div>`;
        if (/^## /.test(line)) return `<div style="font-size:11px;font-weight:700;color:#38bdf8;margin:14px 0 5px;">${escapeUiText(line.replace(/^## /, ""))}</div>`;
        if (/^- /.test(line)) return `<div style="padding:2px 0 2px 10px;color:var(--text,#e2e8f0);line-height:1.35;">${escapeUiText(line.replace(/^- /, "")).replace(/`([^`]+)`/g, "<code>$1</code>")}</div>`;
        if (/^---+$/.test(line)) return `<div style="height:1px;background:rgba(148,163,184,0.14);margin:8px 0;"></div>`;
        if (!line.trim()) return `<div style="height:5px;"></div>`;
        return `<div style="color:var(--muted,#94a3b8);line-height:1.35;">${escapeUiText(line).replace(/`([^`]+)`/g, "<code>$1</code>")}</div>`;
      }).join("");
    }

    function openChangeLogPanel() {
      if (changeLogPanel) { changeLogPanel.querySelector("#sys-changelog-close")?.click(); return; }
      changeLogPanel = document.createElement("div");
      changeLogPanel.id = "sys-changelog-panel";
      Object.assign(changeLogPanel.style, {
        position: "fixed", bottom: "32px", right: "0", width: "640px", maxWidth: "min(640px, calc(100vw - 18px))", maxHeight: "520px",
        background: "var(--panel-bg, #0f172a)", border: "1px solid rgba(148,163,184,0.15)",
        borderRadius: "8px 0 0 8px", overflow: "hidden", display: "flex", flexDirection: "column",
        zIndex: "9001", boxShadow: "0 -4px 24px rgba(0,0,0,0.5)",
      });
      changeLogPanel.innerHTML = `
        <div style="display:flex;align-items:center;gap:8px;padding:7px 12px;border-bottom:1px solid rgba(148,163,184,0.12);flex-shrink:0;">
          <span style="font-size:11px;font-weight:600;color:var(--text,#e2e8f0);flex:1;">Agent Change Log</span>
          <span id="sys-changelog-status" style="font-size:9px;color:var(--muted,#64748b);">Loading...</span>
          <button id="sys-changelog-refresh" style="background:none;border:none;cursor:pointer;color:var(--muted,#64748b);font-size:10px;padding:0 2px;line-height:1;">Refresh</button>
          <button id="sys-changelog-close" style="background:none;border:none;cursor:pointer;color:var(--muted,#64748b);font-size:13px;padding:0 2px;line-height:1;">x</button>
        </div>
        <div id="sys-changelog-entries" style="overflow-y:auto;flex:1;font-size:10px;padding:10px 12px;"></div>`;
      document.body.appendChild(changeLogPanel);
      const closeChangeLogPanel = () => {
        changeLogPanel?.remove();
        changeLogPanel = null;
      };
      changeLogPanel.querySelector("#sys-changelog-close")?.addEventListener("click", closeChangeLogPanel);

      async function loadChangeLog() {
        const statusEl = changeLogPanel?.querySelector("#sys-changelog-status");
        const listEl = changeLogPanel?.querySelector("#sys-changelog-entries");
        if (!listEl) return;
        try {
          if (statusEl) statusEl.textContent = "Loading...";
          const r = await fetch("/docs/AGENT_CHANGELOG.md", { cache: "no-store" });
          const text = await r.text();
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          if (!changeLogPanel) return;
          listEl.innerHTML = formatChangeLogMarkdown(text);
          if (statusEl) statusEl.textContent = "Loaded";
        } catch (err) {
          if (statusEl) statusEl.textContent = "Failed";
          listEl.innerHTML = `<div style="padding:16px;color:#fca5a5;text-align:center;">${escapeUiText(err?.message || "Failed to load changelog")}</div>`;
        }
      }

      changeLogPanel.querySelector("#sys-changelog-refresh")?.addEventListener("click", loadChangeLog);
      loadChangeLog();
    }

    bar.addEventListener("click", e => {
      const btn = e.target.closest("[data-fb-open]");
      if (btn) { openFb(btn.getAttribute("data-fb-open")); return; }
      if (e.target.closest("[data-sys-action='context']")) { openContextPanel(); return; }
      if (e.target.closest("[data-sys-action='changelog']")) { openChangeLogPanel(); return; }
      if (e.target.closest("[data-sys-action='log']")) { openLogPanel(); return; }
    });

    const appBody = rootEl.querySelector(".app-body");
    rootEl.insertBefore(bar, appBody);
    render();
    fetchMetrics();
    // Delay speedtest so 10 MB download doesn't compete with initial data fetches
    setTimeout(measureSpeed, 12000);
    metricsTimer = setInterval(() => { if (!document.hidden) fetchMetrics(); }, 30000);
    speedTimer = setInterval(() => { if (!document.hidden) measureSpeed(); }, 60000);
  }

  events.on("state:changed", ({ meta }) => {
    if (meta?.reason === "reset") toast("State reset");
  });

  addResizer(leftPanelEl, "left", "--leftPanelW", LEFT_W_KEY);
  addResizer(rightPanelEl, "right", "--rightPanelW", RIGHT_W_KEY);
  addResizer(bottomPanelEl, "top", "--bottomPanelH", BOTTOM_H_KEY);
  ensureThemeToggle();
  ensureCaptureButton();
  ensureDockToggles();
  window.addEventListener("resize", () => {
    reconcilePanelSizes();
  });
  ensureSettingsModal();
  applyUiTheme(localStorage.getItem(UI_THEME_KEY) || "deep-blue");
  ensureSettingsButton();
  ensureSimulatorFullscreenHandoff();
  ensureFullscreenButton();
  ensureGlobalFileDrop();
  configureAutosave();
  ensureSysBar();

  return {
    bindRuntime,
    addTopbarButton,
    mountLeftPanel,
    mountRightPanel,
    mountBottomPanel,
    unmountBottomPanel,
    showBottomPanel,
    toast,
    setDockOpen,
  };
}
